// Work Management shared UI — native work items (definition + lifecycle), as
// opposed to workassignments (field execution/presence). KaziPeople global theme
// tokens only: muted/accent are SURFACES, never bare text colors.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CancelWorkDialog } from './CancelWorkDialog';
import { UnassignWorkDialog } from './UnassignWorkDialog';
import { RemoveWorkDialog, restoreWithFeedback } from './RemoveWorkDialog';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowRightLeft, UserMinus, CheckCircle2, Circle, Copy, Download, History, Link2 as LinkIcon, Loader2, MessageSquare, Paperclip, Pencil, Play, Send, ThumbsUp, Undo2, Upload, UserCheck, Users, X } from 'lucide-react';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { Input } from '@/features/shared/components/ui/input';
import { DataState } from '@/features/shared/components/ui/DataState';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import { useConfirmIfAvailable } from '@/features/shared/components/confirm/ConfirmProvider';
import { RecognizeInline } from '@/features/shared/components/RecognizeInline';
import { RecognitionSuggestions } from '@/features/shared/components/RecognitionSuggestions';
import { useAppSelector } from '@/app/hooks';
import { selectUser } from '@/features/auth/state/auth-state/authSlice';
import { DateTimeField } from '@/features/shared/components/DateTimeField';
import { getApiErrorMessage } from '@/lib/apiError';
import i18n from '@/i18n/config';
import { getMyProfile } from '@/lib/hrApi';
import { Link } from 'react-router-dom';
import { AssignmentType, createAssignment, getAssignment, listTypes } from '@/services/workAssignmentService';
import { Goal, listGoals } from '@/services/performanceService';
import { fmtDT } from '../workassignments/shared';
import { WorkAssetsSection } from './WorkAssets';
import { EffortChip, EffortSelect, HealthChip } from './workload/health';
import { useHasPermission } from '@/rbac/useRbac';
import { PeoplePicker, type Person } from '@/features/shared/components/PeoplePicker';
import { rememberPersonName, useEligibleAssignees, useMyEmployeeId, usePeopleNames, useWorkPolicy } from '@/features/work/api/hooks';
import {
  AssignmentHistoryEntry, CompletionPolicy, CompletionReview, EffortSize, EligiblePage,
  EvidenceRequirement, WorkEvidence, WorkItem, WorkItemDetail, WorkProject, WorkStatus, WorkTeamView, WorkTemplate, WorkViewer,
  AssetPurpose,
  addNoteEvidence, addWorkComment, addWorkContributor, approveWork, assignWorkItem, cancelWork, createAckInvite, createProject,
  createWorkItem, createRecurringWorkItem, createWorkTemplate, duplicateWorkItem, getWorkItem, getWorkTeam, holdWork, linkExecution, listProjects,
  listWorkItems, listWorkTemplates, openEvidence, reassignWorkItem, removeWorkContributor, reopenWork, requestChanges, startWork, stopWorkSeries,
  submitWork, toggleChecklist, updateWorkItem, updateWorkTemplate, uploadEvidence,
  addWorkAssetLink, uploadWorkAsset, downloadWorkAssetVersion, WorkActivity,
} from '@/services/workService';

// A staged asset in the create dialog, attached after the item is created.
type PendingAsset =
  | { kind: 'link'; name: string; url: string; purpose: AssetPurpose }
  | { kind: 'file'; name: string; file: File; purpose: AssetPurpose };

export const WorkStatusBadge: React.FC<{ status: WorkStatus }> = ({ status }) => {
  const cls: Record<string, string> = {
    draft: 'bg-muted text-foreground',
    planned: 'bg-muted text-foreground',
    assigned: 'bg-muted text-muted-foreground',
    started: 'bg-info-soft text-info-strong',
    in_progress: 'bg-info-soft text-info-strong',
    submitted_for_completion: 'bg-warning-soft text-warning-strong',
    under_review: 'bg-warning-soft text-warning-strong',
    changes_requested: 'bg-danger-soft text-danger-strong',
    completed: 'bg-success-soft text-success-strong',
    on_hold: 'bg-muted text-muted-foreground',
    cancelled: 'bg-muted text-muted-foreground line-through',
    reopened: 'bg-muted text-muted-foreground',
  };
  const label = status === 'submitted_for_completion' ? 'submitted' : status.replace(/_/g, ' ');
  return <Badge variant="secondary" className={cls[status] || 'bg-muted text-foreground'}>{label}</Badge>;
};

// Status filter chips for the work-item lists (Work Review, My Work). Each key is
// the comma-separated status string sent to the list API; the label is friendly.
export const WORK_STATUS_FILTERS = [
  { key: 'draft', label: 'Draft' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'started,in_progress', label: 'In progress' },
  { key: 'submitted_for_completion,under_review', label: 'Submitted' },
  { key: 'changes_requested', label: 'Changes' },
  { key: 'on_hold', label: 'On hold' },
  { key: 'completed', label: 'Completed' }, // US-21: canonical vocabulary (key stays the API enum)
  { key: 'cancelled', label: 'Cancelled' },
];

// Stored planned dates are RFC3339 (UTC); DateTimeField speaks local
// "YYYY-MM-DDTHH:mm". Convert for prefill.
const isoToLocalInput = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Pre-work states from which a work item can be assigned (mirrors the backend Assign
// transition's allowed from-set). Assigning is what moves a Draft forward — not adding
// contributors.
export const ASSIGNABLE_STATUSES: WorkStatus[] = ['draft', 'planned', 'reopened'];

/** Whether the Draft assign control should be shown: the user may assign (work:assign),
 *  the item has no accountable owner yet, and it's in an assignable pre-work state. */
export function canShowAssign(status: WorkStatus, ownerEmployeeId: string | null | undefined, canAssign: boolean): boolean {
  return canAssign && !ownerEmployeeId && ASSIGNABLE_STATUSES.includes(status);
}

// Active states where an already-owned item may be REASSIGNED to a different owner
// without changing lifecycle status (mirrors the backend reassignableStates,
// which includes `reopened` — the item most likely to need a different person).
export const REASSIGNABLE_STATUSES: WorkStatus[] = ['assigned', 'started', 'in_progress', 'on_hold', 'changes_requested', 'reopened'];

/** Whether the reassign control should be shown: the user may assign, the item already
 *  has an owner, and it's in an active (non-terminal) state. */
export function canReassign(status: WorkStatus, ownerEmployeeId: string | null | undefined, canAssign: boolean): boolean {
  return canAssign && !!ownerEmployeeId && REASSIGNABLE_STATUSES.includes(status);
}

/** A correction affordance (reassign / cancel / …) for ONE item. The server's
 *  per-item `viewer` flag wins — it is evaluated with the same rule the action
 *  enforces (owner / creator / manager of the owner / work admin), so the UI
 *  never offers what will be refused or hides what would work. Older backends
 *  omit the flag; the caller's previous client-side check is the fallback. */
export function viewerAllows(flag: boolean | undefined, fallback: boolean): boolean {
  return typeof flag === 'boolean' ? flag : fallback;
}

// States where the server would still let SOMEONE unassign the item, but no
// longer its owner: the owner may hand work back only while it is `assigned`
// (work-management-service unassign.go, D4). Mirrors unassignableStates minus
// the pre-start ones.
export const HAND_BACK_LOCKED_STATUSES: WorkStatus[] = ['started', 'in_progress', 'on_hold', 'changes_requested', 'reopened'];

/** W14: Hand back stays VISIBLE but disabled once the owner has started the
 *  item, so it explains itself instead of vanishing. True only when the server
 *  said no (`can_unassign === false`) to the item's owner, who has work:write,
 *  for the post-start reason alone. Anyone who could never hand back — not the
 *  owner, no permission, an older backend without the flag, or work that is
 *  submitted / finished — still sees nothing. */
export function handBackLockedAfterStart(args: {
  isOwner: boolean;
  canUnassign: boolean | undefined;
  status: WorkStatus | undefined;
  canWrite: boolean;
}): boolean {
  return args.isOwner && args.canWrite && args.canUnassign === false
    && !!args.status && HAND_BACK_LOCKED_STATUSES.includes(args.status);
}

/** W14: the disabled Hand back with its explanation (the assignee, after
 *  starting). One component wherever the detail shows it. */
function HandBackLocked({ workItemId }: { workItemId: string }) {
  const { t } = useTranslation(['work']);
  return (
    <div className="space-y-1">
      <Button type="button" size="sm" variant="ghost" className="w-full text-muted-foreground"
        disabled aria-describedby={`hand-back-locked-${workItemId}`} data-testid="hand-back-locked">
        <UserMinus className="w-4 h-4 mr-1" />
        {t('hand_back', { defaultValue: 'Hand back' })}
      </Button>
      <p id={`hand-back-locked-${workItemId}`} className="text-[11px] text-muted-foreground">
        {t('hand_back_locked_hint', { defaultValue: 'Hand back is only available before you start the item. Ask your manager to reassign it.' })}
      </p>
    </div>
  );
}

/** The caller's relationship to an item (design §6). Prefers the server's
 * `viewer` (authoritative); older payloads fall back to owner_employee_id ===
 * me and the team's contributor rows. Collaborators never own; a collaborator
 * who owns a sub-item sees that sub-item's viewer as owner. */
export interface ViewerRole { isOwner: boolean; isCollaborator: boolean; canAddCollaborators: boolean }
export function resolveViewerRole(args: {
  viewer?: WorkViewer | null;
  ownerEmployeeId?: string | null;
  contributorIds?: string[];
  myEmployeeId?: string | null;
  canAssign: boolean;
}): ViewerRole {
  const { viewer, ownerEmployeeId, contributorIds = [], myEmployeeId, canAssign } = args;
  if (viewer) {
    return {
      isOwner: !!viewer.is_owner,
      isCollaborator: !!viewer.is_collaborator && !viewer.is_owner,
      canAddCollaborators: !!viewer.can_add_collaborators || canAssign,
    };
  }
  const isOwner = !!ownerEmployeeId && !!myEmployeeId && ownerEmployeeId === myEmployeeId;
  const isCollaborator = !isOwner && !!myEmployeeId && contributorIds.includes(myEmployeeId);
  return { isOwner, isCollaborator, canAddCollaborators: canAssign };
}

/** Owner-only lifecycle actions (Start / Submit / Hold / cancel own sub-item).
 * A collaborator never gets them; without a server viewer (older backend) or
 * on an item that has no owner yet, the actor view keeps today's affordances —
 * the backend state machine enforces either way. */
export function ownerActs(role: ViewerRole, hasViewer: boolean, ownerEmployeeId?: string | null): boolean {
  if (role.isCollaborator) return false;
  if (!hasViewer) return true;
  return role.isOwner || !ownerEmployeeId;
}

const CAN_START: WorkStatus[] = ['draft', 'assigned', 'reopened', 'changes_requested', 'on_hold'];
const CAN_SUBMIT: WorkStatus[] = ['started', 'in_progress', 'changes_requested'];
const CAN_REVIEW: WorkStatus[] = ['submitted_for_completion', 'under_review'];
const WORKABLE: WorkStatus[] = ['assigned', 'started', 'in_progress', 'changes_requested', 'reopened'];

const reqSatisfied = (r: EvidenceRequirement, evidence: WorkEvidence[]) =>
  evidence.some((e) => e.requirement_id === r.id);

// ---- Assignment history (design §6) ----
// A minimal translate signature so the sentence formatter stays a pure function
// (unit-testable with a stub `t`) while the page passes react-i18next's `t`.
export type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** One history row as a sentence. Reassignments name both people; initial
 * assignments only the new owner; subtask variants say "subtask". */
export function historySentence(e: AssignmentHistoryEntry, t: Translate): string {
  const by = e.changed_by_name || t('history_someone', { defaultValue: 'Someone' });
  const to = e.new_name || t('history_unknown_person', { defaultValue: 'an unassigned owner' });
  const from = e.previous_name || t('history_unknown_person', { defaultValue: 'an unassigned owner' });
  switch (e.kind) {
    case 'reassign':
      return t('history_reassigned', { defaultValue: '{{by}} reassigned this work from {{from}} to {{to}}', by, from, to });
    case 'subtask_reassign':
      return t('history_subtask_reassigned', { defaultValue: '{{by}} reassigned this subtask from {{from}} to {{to}}', by, from, to });
    case 'subtask_assign':
      return t('history_subtask_assigned', { defaultValue: '{{by}} assigned this subtask to {{to}}', by, to });
    case 'unassign':
    case 'subtask_unassign': {
      // The owner removing themselves is a hand-back, and reads as one.
      const handBack = !!e.previous_user_id && e.previous_user_id === e.changed_by_user_id;
      const sub = e.kind === 'subtask_unassign';
      if (handBack) {
        return sub
          ? t('history_subtask_handed_back', { defaultValue: '{{from}} handed this subtask back', from })
          : t('history_handed_back', { defaultValue: '{{from}} handed this work back', from });
      }
      return sub
        ? t('history_subtask_unassigned', { defaultValue: '{{by}} removed {{from}} from this subtask', by, from })
        : t('history_unassigned', { defaultValue: '{{by}} removed {{from}} from this work', by, from });
    }
    case 'assign':
    default:
      return t('history_assigned', { defaultValue: '{{by}} assigned this work to {{to}}', by, to });
  }
}

/** Assignment history panel — hidden entirely when there are no rows. */
export const AssignmentHistorySection: React.FC<{ history?: AssignmentHistoryEntry[] | null; className?: string }> = ({ history, className }) => {
  const { t } = useTranslation('work');
  const rows = history || [];
  if (rows.length === 0) return null;
  return (
    <div className={`rounded-lg border border-border p-4 space-y-2 ${className || ''}`} data-testid="assignment-history">
      <span className="text-sm font-medium text-charcoal inline-flex items-center gap-1.5">
        <History className="w-3.5 h-3.5" />
        {t('assignment_history', { defaultValue: 'Assignment history' })}
      </span>
      <ul className="space-y-1.5">
        {rows.map((e) => (
          <li key={e.id} className="text-xs">
            <p className="text-foreground">{historySentence(e, t as unknown as Translate)}</p>
            {e.reason?.trim() ? (
              <p className="text-muted-foreground italic">{t('history_reason', { defaultValue: 'Reason: {{reason}}', reason: e.reason.trim() })}</p>
            ) : null}
            <p className="text-[11px] text-muted-foreground tabular-nums" title={e.occurred_at}>{fmtDT(e.occurred_at)}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Reassign dialog — moves the accountable owner of an already-assigned item
 * via POST /reassign (never /assign). The backend enforces the tenant policy,
 * the state machine and the caller's assignment scope; its message is toasted
 * verbatim on refusal. */
export const ReassignDialog: React.FC<{
  open: boolean;
  workItemId: string;
  currentOwnerId?: string | null;
  currentOwnerName?: string;
  fetchPage: (q: string, page: number) => Promise<EligiblePage>;
  onClose: () => void;
  /** Fired after a successful reassignment (the page reloads the item). */
  onDone: (item: WorkItem) => void;
  /** `assign` = INITIAL assignment of an unowned draft via POST /assign (no
   * reason field); default `reassign` moves an existing owner via POST /reassign. */
  mode?: 'assign' | 'reassign';
  /** Copy variant for a sub-item (Phase 4): "Assign subtask" / "Reassign subtask". */
  subtask?: boolean;
  /** Prefill (Workload suggestions): the proposed new owner + a default reason.
   * Both stay editable — a suggestion never bypasses the picker. */
  initialTarget?: Person | null;
  initialReason?: string;
  /** The item has open sub-items: offer "move the ones still with the current
   *  owner too" (on by default — they inherited that owner from this item). */
  hasOpenSubitems?: boolean;
  /** The item's updated_at as displayed — the server refuses (409) if it changed since. */
  expectedUpdatedAt?: string;
}> = ({ open, workItemId, currentOwnerId, currentOwnerName, fetchPage, onClose, onDone, mode = 'reassign', subtask = false, initialTarget, initialReason, hasOpenSubitems = false, expectedUpdatedAt }) => {
  const { t } = useTranslation('work');
  const [target, setTarget] = useState<Person | null>(initialTarget ?? null);
  const [reason, setReason] = useState(initialReason ?? '');
  const [moveSubtasks, setMoveSubtasks] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTarget(initialTarget ?? null); setReason(initialReason ?? ''); setMoveSubtasks(true); setBusy(false); } }, [open, workItemId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const assigning = mode === 'assign';

  const submit = async () => {
    if (!target || busy) return;
    setBusy(true);
    try {
      rememberPersonName(target.employee_id, target.display_name);
      const item = assigning
        ? await assignWorkItem(workItemId, target.employee_id, target.user_id || undefined)
        : await reassignWorkItem(workItemId, {
          employee_id: target.employee_id,
          user_id: target.user_id || undefined,
          reason: reason.trim() || undefined,
          ...(hasOpenSubitems ? { move_subtasks: moveSubtasks } : {}),
          ...(expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}),
        });
      toast.success((assigning
        ? t('assigned_to', { defaultValue: 'Assigned to {{name}}', name: target.display_name })
        : t('reassigned_to', { defaultValue: 'Reassigned to {{name}}', name: target.display_name })) as string);
      onDone(item);
      onClose();
    } catch (e) {
      // 403 from the cross-team subtask policy / assignment scope carries a
      // plain `message` — surface it verbatim (backend-error-surfacing rule).
      toast.error(getApiErrorMessage(e, assigning
        ? t('assign_failed', { defaultValue: 'Could not assign this work.' })
        : t('reassign_failed', { defaultValue: 'Could not reassign this work.' })));
    } finally {
      setBusy(false);
    }
  };

  const title = assigning
    ? (subtask ? t('assign_subtask_title', { defaultValue: 'Assign subtask' }) : t('assign_title', { defaultValue: 'Assign work' }))
    : (subtask ? t('reassign_subtask_title', { defaultValue: 'Reassign subtask' }) : t('reassign_title', { defaultValue: 'Reassign work' }));
  const hint = assigning
    ? t('assign_subtask_hint', { defaultValue: 'The person you pick becomes the accountable owner of this subtask.' })
    : currentOwnerName
      ? t('reassign_hint_named', { defaultValue: 'Currently assigned to {{name}}. The new owner takes over — the status does not change.', name: currentOwnerName })
      : t('reassign_hint', { defaultValue: 'The new owner takes over — the status does not change.' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/20 p-4" onClick={() => { if (!busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby={`reassign-title-${workItemId}`}
        className="w-full max-w-md rounded-lg bg-background border border-border p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}>
        <h3 id={`reassign-title-${workItemId}`} className="text-h4 text-charcoal">{title}</h3>
        <p className="text-xs text-muted-foreground">{hint}</p>
        <div>
          <Label htmlFor={`reassign-${workItemId}`}>
            {assigning ? t('assign_to', { defaultValue: 'Assign to' }) : t('reassign_to', { defaultValue: 'Reassign to' })}
          </Label>
          <PeoplePicker id={`reassign-${workItemId}`} value={target} onChange={setTarget}
            fetchPage={fetchPage} disabled={busy}
            exclude={currentOwnerId ? [currentOwnerId] : undefined}
            placeholder={t('select_assignee', { defaultValue: 'Select an employee…' }) as string} />
        </div>
        {!assigning && (
          <div>
            <Label htmlFor={`reassign-reason-${workItemId}`}>{t('reassign_reason', { defaultValue: 'Reason (optional)' })}</Label>
            <Textarea id={`reassign-reason-${workItemId}`} rows={2} value={reason} disabled={busy}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('reassign_reason_ph', { defaultValue: 'Why is this work moving? Shown in the history.' }) as string} />
          </div>
        )}
        {!assigning && hasOpenSubitems && (
          <label className="flex items-start gap-2 text-sm text-foreground" htmlFor={`reassign-subtasks-${workItemId}`}>
            <Checkbox id={`reassign-subtasks-${workItemId}`} checked={moveSubtasks} disabled={busy}
              onCheckedChange={(v) => setMoveSubtasks(v === true)} className="mt-0.5" />
            <span>
              {currentOwnerName
                ? t('reassign_move_subtasks_named', { defaultValue: 'Also move the open sub-items still assigned to {{name}}', name: currentOwnerName })
                : t('reassign_move_subtasks', { defaultValue: 'Also move the open sub-items still assigned to the current owner' })}
              <span className="block text-xs text-muted-foreground">
                {t('reassign_move_subtasks_help', { defaultValue: 'Sub-items someone else owns, and finished ones, stay as they are.' })}
              </span>
            </span>
          </label>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}>{t('cancel', { defaultValue: 'Cancel' })}</Button>
          <Button size="sm" disabled={busy || !target} onClick={submit}>
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : assigning ? <UserCheck className="w-4 h-4 mr-1" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />}
            {assigning ? t('assign', { defaultValue: 'Assign' }) : t('reassign', { defaultValue: 'Reassign' })}
          </Button>
        </div>
      </div>
    </div>
  );
};

/** Role chip next to a person in the Team panel / list rows: "Owner" (the ONE
 * accountable person) or "Collaborator". */
export const RoleChip: React.FC<{ role: 'owner' | 'collaborator'; className?: string }> = ({ role, className = '' }) => {
  const { t } = useTranslation('work');
  const cls = role === 'owner'
    ? 'border-orange-300 text-orange-700 dark:border-orange-500/40 dark:text-orange-300'
    : 'border-border text-muted-foreground';
  return (
    <span data-testid={`role-chip-${role}`}
      className={`inline-flex items-center rounded border px-1 py-px text-[10px] font-medium uppercase tracking-wide leading-none ${cls} ${className}`}>
      {role === 'owner' ? t('role_owner', { defaultValue: 'Owner' }) : t('role_collaborator', { defaultValue: 'Collaborator' })}
    </span>
  );
};

/** WorkItemSurface — the full working view of one work item, rendered as its
 * own routed page (My Work / Work Review both navigate here; sub-items navigate
 * by URL so the browser back button works and any item can be deep-linked).
 * The backend state machine + completion gate + SoD are authoritative — every
 * action just calls it and surfaces the backend message on refusal. */
export const WorkItemSurface: React.FC<{
  id: string;
  mode: 'actor' | 'reviewer';
  openItem: (id: string) => void;
  onChanged: () => void;
  // Reports the loaded work item's title up (so the page can label a "Discuss"
  // conversation with the item's real name instead of a generic "Work item").
  onTitle?: (title: string) => void;
  // Reports the loaded item up (the page derives reviewer-vs-actor from its owner).
  onLoaded?: (item: WorkItem) => void;
}> = ({ id, mode, openItem, onChanged, onTitle, onLoaded }) => {
  const { t } = useTranslation('work');
  const [children, setChildren] = useState<WorkItem[]>([]);
  const [subTitle, setSubTitle] = useState('');
  const [subOwner, setSubOwner] = useState<Person | null>(null);
  const [team, setTeam] = useState<WorkTeamView | null>(null);
  const [newContributor, setNewContributor] = useState<Person | null>(null);
  const [assignee, setAssignee] = useState<Person | null>(null);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [unassignOpen, setUnassignOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  // Sub-item whose owner is being (re)assigned from its row (Phase 4); the
  // dialog picks assign vs reassign from whether it already has an owner.
  const [subAssign, setSubAssign] = useState<WorkItem | null>(null);
  const canAssign = useHasPermission('work:assign');
  // Restore is a work:remove decision; the server re-checks it on every call.
  const canRemoveWork = useHasPermission('work:remove');
  const canWriteWork = useHasPermission('work:write');
  const myEmployeeId = useMyEmployeeId();
  // Tenant policy + my resolved capabilities: reassignment is hidden when the
  // tenant switched it off or when the policy says I cannot assign at all
  // (work:assign alone is not enough — the policy is authoritative).
  const { policy: workPolicy, me: workMe } = useWorkPolicy();
  const reassignAllowed = workMe?.can_assign !== false && workPolicy?.allow_reassignment !== false;
  // Eligible people come from the server per purpose (assign / collaborate /
  // subtask) scoped to THIS item — never a roster download.
  const fetchAssignees = useEligibleAssignees('assign', { workItemId: id });
  const fetchCollaborators = useEligibleAssignees('collaborate', { workItemId: id });
  const fetchSubtaskOwners = useEligibleAssignees('subtask', { workItemId: id });
  // Eligibility for the sub-item being (re)assigned is scoped to THAT sub-item
  // (the server applies the cross-team subtask policy against its parent).
  const fetchSubtaskAssignees = useEligibleAssignees('subtask', { workItemId: subAssign?.id || id });
  const [detail, setDetail] = useState<WorkItemDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [evNote, setEvNote] = useState('');
  const [evFor, setEvFor] = useState<string>('');
  const [fileFor, setFileFor] = useState<string>('');
  const [effort, setEffort] = useState<string>('');
  const [ackFor, setAckFor] = useState<string>('');
  const [ackName, setAckName] = useState('');
  const [ackEmail, setAckEmail] = useState('');
  const [ackLink, setAckLink] = useState('');
  const [execTypes, setExecTypes] = useState<AssignmentType[]>([]);
  const [execType, setExecType] = useState('');
  const [comment, setComment] = useState('');
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editPriority, setEditPriority] = useState('normal');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [editEffort, setEditEffort] = useState<EffortSize | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  // Branded confirm (never window.confirm). Null when rendered standalone —
  // the action that needs it (Stop repeating) is then simply not offered.
  const confirm = useConfirmIfAvailable();

  // Why the item could not be loaded, shown IN the page (W27/W29): a toast
  // fired once per load attempt stacked up (the load re-runs when the
  // translation namespace arrives) while the body spun forever on `!w`.
  const [loadError, setLoadError] = useState<{ removed: boolean; message: string } | null>(null);
  // A refused Restore, shown in the removed banner itself (W23) — not only as a
  // toast, which was easy to miss on the item page.
  const [restoreError, setRestoreError] = useState('');

  const load = useCallback(() => {
    // include_removed: an administrator opening a removed item sees it with a
    // banner and Restore; anyone else gets the server's 410 message.
    getWorkItem(id, { includeRemoved: true }).then((d) => { setLoadError(null); setDetail(d); onLoaded?.(d.work_item); })
      .catch((e) => setLoadError({
        removed: (e as { response?: { status?: number } })?.response?.status === 410,
        message: getApiErrorMessage(e, t('load_failed', { defaultValue: 'Could not load the work item.' })),
      }));
    listWorkItems({ parent_id: id, limit: 50 }).then((p) => setChildren(p.items)).catch(() => setChildren([]));
    getWorkTeam(id).then(setTeam).catch(() => setTeam(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);
  useEffect(() => {
    setDetail(null); setLoadError(null); setRestoreError(''); setChildren([]); setNote(''); setEvNote(''); setEvFor(''); setFileFor('');
    setSubTitle(''); setComment(''); setEditing(false); setAckLink('');
    setAssignee(null); setNewContributor(null); setSubOwner(null); setReassignOpen(false); setUnassignOpen(false); setSubAssign(null);
    load();
  }, [load]);
  // Display names for everyone this item references (owner, contributors,
  // sub-item owners) — resolved per id via the directory-safe /names lookup.
  const { nameOf } = usePeopleNames([
    detail?.work_item.owner_employee_id,
    ...(team?.contributors || []).map((c) => c.employee_id),
    ...(team?.contributions || []).map((c) => c.employee_id),
    ...children.map((c) => c.owner_employee_id),
  ]);
  // Field-execution types the employee may self-create (actor mode only).
  useEffect(() => {
    if (mode !== 'actor') return;
    listTypes().then((ts) => {
      const ok = ts.filter((x) => x.active && x.allowsSelfCreate);
      setExecTypes(ok);
      if (ok.length) setExecType((cur) => cur || ok[0].key);
    }).catch(() => undefined);
  }, [mode]);

  // Linked field execution: pre-fill "Hours" from the checked-in → checked-out
  // span, so the employee submits what they actually worked (still editable).
  const linkKey = (detail?.execution_links || []).map((l) => l.assignment_id).join(',');
  useEffect(() => {
    if (mode !== 'actor' || !linkKey) return;
    let cancelled = false;
    Promise.all(linkKey.split(',').map((aid) =>
      getAssignment(aid).then((r) => r.assignment.workedMinutes ?? 0).catch(() => 0)))
      .then((mins) => {
        const total = mins.reduce((sum, m) => sum + m, 0);
        if (!cancelled && total > 0) setEffort((cur) => cur || String(Math.round((total / 60) * 100) / 100));
      });
    return () => { cancelled = true; };
  }, [linkKey, mode]);

  const w = detail?.work_item;
  // Lift the item's title so the page can name a "Discuss" conversation after it.
  useEffect(() => {
    if (w?.title) onTitle?.(w.title);
  }, [w?.title, onTitle]);
  const checklist = detail?.checklist || [];
  const reqs = detail?.evidence_requirements || [];
  const startReqs = reqs.filter((r) => r.phase === 'start');
  const completionReqs = reqs.filter((r) => r.phase !== 'start');
  const evidence = detail?.evidence || [];
  const allActivity = detail?.activity || [];
  const comments = allActivity.filter((a) => a.verb === 'comment').slice().reverse();
  const myUserId = useAppSelector(selectUser)?.id;
  const timeline = allActivity.filter((a) => a.verb !== 'comment').slice(0, 8);
  const subDone = children.filter((c) => c.status === 'completed').length;
  const subOpen = children.filter((c) => !['completed', 'cancelled'].includes(c.status)).length;
  // Does finishing hand the work to a reviewer? The SERVER already resolved
  // item → work type → company policy into `review_required` — render its
  // decision, never re-derive the rule here. Older backends omit it, and review
  // was always required then.
  const reviewRequired = w?.review_required !== false;
  // Definition fields are editable until submission freezes the reviewed content.
  const canEdit = !!w && !w.removed_at && !['submitted_for_completion', 'under_review', 'completed', 'cancelled'].includes(w.status);
  // Owner vs collaborator (design §6): the server's `viewer` decides; older
  // payloads derive it from the owner id + contributor rows.
  const contributorRows = team?.contributors || [];
  const role = resolveViewerRole({
    viewer: detail?.viewer,
    ownerEmployeeId: w?.owner_employee_id,
    contributorIds: contributorRows.map((c) => c.employee_id),
    myEmployeeId,
    canAssign,
  });
  const actsAsOwner = mode === 'actor' && ownerActs(role, !!detail?.viewer, w?.owner_employee_id);
  const collabHint = mode === 'actor' && role.isCollaborator && !!w && !['completed', 'cancelled'].includes(w.status);
  // Adding/removing collaborators: work:assign OR the server says the owner/creator may.
  const canAddCollaborators = role.canAddCollaborators || (workMe?.can_assign === true);
  const contribName = (c: { employee_id: string; name?: string | null }) => c.name || nameOf(c.employee_id);
  // ---- Sub-items (design §6 / Phase 4) ----
  // Progress prefers the server rollup (`subitems` + `progress`, which also
  // covers children outside the 50-row page) and falls back to the loaded rows.
  const subTotal = detail?.subitems?.total ?? children.length;
  const subDoneCount = detail?.subitems?.done ?? subDone;
  const subProgress = subTotal > 0
    ? Math.max(0, Math.min(100, Math.round((w?.progress || 0) > 0 ? (w?.progress as number) : (subDoneCount / subTotal) * 100)))
    : 0;
  const completionPolicy: CompletionPolicy = w?.completion_policy || 'manual';
  // The server refuses submission under require_subitems while any child is
  // neither completed nor cancelled; the hint mirrors that (Submit stays
  // enabled — the backend decides). Loaded rows give the exact open count;
  // the rollup approximates it when the child page hasn't loaded.
  const submitBlockedBySub = completionPolicy === 'require_subitems'
    && (children.length > 0 ? subOpen > 0 : subDoneCount < subTotal);
  // Completion policy is an owner/reviewer/creator field (same authorization as
  // editing the title); older payloads without a viewer fall back to the
  // derived owner role.
  const canSetCompletionPolicy = !!w && (
    detail?.viewer ? (!!detail.viewer.is_owner || !!detail.viewer.can_review) : role.isOwner
  ) || (!!w?.created_by && !!myUserId && w.created_by === myUserId);
  // (Re)assigning a sub-item from its row: policy-level can_assign OR the
  // parent's owner/creator who may manage its team (viewer.can_add_collaborators).
  const canAssignSubtasks = workMe?.can_assign === true || !!detail?.viewer?.can_add_collaborators;
  // Correction affordances: the server's per-item flags decide (same rule the
  // action enforces); older payloads keep the previous client-side checks.
  const viewer = detail?.viewer;
  const canReassignItem = !!w && viewerAllows(viewer?.can_reassign,
    canReassign(w.status, w.owner_employee_id, canAssign) && reassignAllowed);
  const handBackLocked = !!w && handBackLockedAfterStart({
    isOwner: role.isOwner, canUnassign: viewer?.can_unassign, status: w.status, canWrite: canWriteWork,
  });
  // The Team card (which normally carries Hand back) is hidden for a solo
  // item the viewer can't staff. W14 follow-up: the locked Hand back and its
  // explanation must not disappear with it — see the standalone card below.
  const teamCardShown = !!w && (w.work_type === 'team' || contributorRows.length > 0 || canAssign
    || canAddCollaborators || role.isCollaborator);
  const canCancelItem = !!w && viewerAllows(viewer?.can_cancel,
    ((mode === 'reviewer') || actsAsOwner) && !['completed', 'cancelled', 'submitted_for_completion', 'under_review'].includes(w.status));
  // What a cascading cancel would stop, at every depth (server count; the
  // loaded direct children approximate it on older backends).
  const openSubitems = detail?.subitems?.open_descendants ?? subOpen;
  const stopSeries = async () => {
    if (!viewer?.series_id || !confirm) return;
    const ok = await confirm({
      title: t('stop_series_title', { defaultValue: 'Stop this repeating work?' }) as string,
      message: t('stop_series_body', { defaultValue: 'No new occurrences will be created. Work already created stays as it is — cancel or reassign those one by one.' }) as string,
      confirmLabel: t('stop_series', { defaultValue: 'Stop repeating' }) as string,
      cancelLabel: t('keep', { defaultValue: 'Keep it' }) as string,
      danger: true,
    });
    if (!ok) return;
    void run(() => stopWorkSeries(viewer.series_id as string), t('stop_series_ok', { defaultValue: 'This work will no longer repeat.' }) as string);
  };

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      load();
      onChanged();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('action_failed', { defaultValue: 'Action failed.' })));
    } finally {
      setBusy(false);
    }
  };

  // Restore reports what it did (and any notes) itself; refusals verbatim.
  const restore = async () => {
    if (!w) return;
    setBusy(true);
    setRestoreError('');
    const r = await restoreWithFeedback(w.id, t, setRestoreError);
    setBusy(false);
    if (r) { load(); onChanged(); }
  };

  if (!w && loadError) {
    // ONE clear state, never a spinner: removed (410) says so without a retry;
    // anything else offers a bounded manual Retry. A work administrator told
    // "removed" still gets Restore (W29) — the read that would have shown the
    // banner failed, but the restore itself does not depend on it.
    const restoreAnyway = loadError.removed && canRemoveWork ? (
      <div className="flex flex-col items-center gap-1">
        <Button size="sm" variant="outline" disabled={busy} data-testid="restore-unloaded"
          onClick={async () => {
            setBusy(true);
            setRestoreError('');
            const r = await restoreWithFeedback(id, t, setRestoreError);
            setBusy(false);
            if (r) { load(); onChanged(); }
          }}>
          <Undo2 className="w-4 h-4 mr-1" />{t('restore', { defaultValue: 'Restore' })}
        </Button>
        {restoreError && <p role="alert" className="text-xs font-medium text-danger-strong">{restoreError}</p>}
      </div>
    ) : undefined;
    return loadError.removed
      ? <DataState variant="unavailable" title={t('removed_gone_title', { defaultValue: 'This work item was removed' }) as string} message={loadError.message} action={restoreAnyway} />
      : <DataState variant="error" message={loadError.message} onRetry={() => { setLoadError(null); load(); }}
          retryLabel={t('common.retry', { defaultValue: 'Retry' }) as string} />;
  }
  if (!w) return <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />;

  const secTitle = 'text-sm font-medium text-charcoal';

  return (
    <div className="space-y-4">
      {/* Removed (work correction P2): only administrators ever see a removed
          item; it is read-only until restored. */}
      {w.removed_at && (
        <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-danger-soft p-3" data-testid="removed-banner">
          <div className="text-sm text-foreground">
            <p className="font-medium">
              {t('removed_banner', { defaultValue: 'Removed {{when}} by {{who}}', when: fmtDT(w.removed_at), who: w.removed_by_name || t('history_someone', { defaultValue: 'Someone' }) })}
            </p>
            <p className="text-xs text-muted-foreground">
              {t(`remove_reason_${w.removal_reason_code || 'other'}`, { defaultValue: w.removal_reason_code || '' })}
              {w.removal_note ? ` — ${w.removal_note}` : ''}
              {' · '}{t('removed_banner_help', { defaultValue: 'Hidden from every list, dashboard and report. Restore brings it back exactly as it was.' })}
            </p>
            {restoreError && <p role="alert" className="mt-1 text-xs font-medium text-danger-strong">{restoreError}</p>}
          </div>
          {viewer?.can_restore && (
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => void restore()}>
              <Undo2 className="w-4 h-4 mr-1" />{t('restore', { defaultValue: 'Restore' })}
            </Button>
          )}
        </div>
      )}
      {/* Header: identity + status */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          {w.parent_id && (
            <button type="button" className="text-xs text-primary-500 hover:underline"
              onClick={() => openItem(w.parent_id as string)}>
              ← {t('back_parent', { defaultValue: 'Parent item' })}
            </button>
          )}
          {!editing ? (
            <div className="flex items-center gap-2">
              <h1 className="text-h3 text-charcoal">{w.title}</h1>
              {canEdit && (
                <button type="button" className="text-muted-foreground hover:text-foreground"
                  title={t('edit_details', { defaultValue: 'Edit details' }) as string}
                  onClick={() => { setEditTitle(w.title); setEditDesc(w.description || ''); setEditPriority(w.priority); setEditStart(isoToLocalInput(w.planned_start_at)); setEditEnd(isoToLocalInput(w.planned_end_at)); setEditEffort(w.effort_size ?? null); setEditing(true); }}>
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </div>
          ) : (
            <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="text-h4" />
          )}
          <p className="text-xs text-muted-foreground tabular-nums mt-0.5">
            {t('updated', { defaultValue: 'Updated' })} {fmtDT(w.updated_at)}
            {w.submitted_at ? ` · ${t('submitted_at', { defaultValue: 'submitted' })} ${fmtDT(w.submitted_at)}` : ''}
            {' · '}{w.priority}
          </p>
          {(w.planned_start_at || w.planned_end_at) && (
            <p className="text-xs text-muted-foreground tabular-nums mt-0.5">
              {t('planned', { defaultValue: 'Planned' })}: {w.planned_start_at ? fmtDT(w.planned_start_at) : '—'} → {w.planned_end_at ? fmtDT(w.planned_end_at) : '—'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {children.length > 0 && (
            <Badge variant="secondary" className="bg-muted text-muted-foreground tabular-nums">
              {subDone}/{children.length} {t('sub_done', { defaultValue: 'sub-items done' })}
            </Badge>
          )}
          <EffortChip size={w.effort_size} />
          {w.health && <HealthChip health={w.health.primary_health} explain={w.health.explain} signals={w.health.signals} />}
          {canEdit && (
            <button type="button" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              title={t('duplicate_hint', { defaultValue: 'Create a fresh copy of this task' }) as string}
              disabled={busy}
              onClick={() => run(async () => {
                const copy = await duplicateWorkItem(w.id);
                openItem(copy.id);
              }, t('duplicated', { defaultValue: 'Task duplicated.' }) as string)}>
              <Copy className="w-3.5 h-3.5" /> {t('duplicate', { defaultValue: 'Duplicate' })}
            </button>
          )}
          <WorkStatusBadge status={w.status} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3 items-start">
        {/* ---- Main column: the work itself ---- */}
        <div className="lg:col-span-2 space-y-4">
          {editing ? (
            <div className="space-y-2 rounded-lg border border-border p-4">
              <Label>{t('description', { defaultValue: 'Description' })}</Label>
              <Textarea rows={4} value={editDesc} onChange={(e) => setEditDesc(e.target.value)}
                placeholder={t('desc_ph', { defaultValue: 'What is this work about? Context, links, expectations…' }) as string} />
              <Label>{t('priority', { defaultValue: 'Priority' })}</Label>
              <select className="w-40 rounded-md border border-border bg-background text-foreground text-sm px-2.5 py-2"
                value={editPriority} onChange={(e) => setEditPriority(e.target.value)}>
                {['low', 'normal', 'high', 'urgent'].map((pr) => <option key={pr} value={pr}>{pr}</option>)}
              </select>
              <Label htmlFor="edit-effort">{t('effort', { defaultValue: 'Effort' })}</Label>
              <EffortSelect id="edit-effort" value={editEffort} onChange={setEditEffort}
                className="w-56 rounded-md border border-border bg-background text-foreground text-sm px-2.5 py-2" />
              <p className="text-xs text-muted-foreground">{t('effort_help', { defaultValue: 'Relative size for workload planning — not hours.' })}</p>
              <div className="space-y-2">
                <div>
                  <Label>{t('planned_start', { defaultValue: 'Planned start' })}</Label>
                  <DateTimeField value={editStart} onChange={(v) => { setEditStart(v); if (editEnd && v && new Date(editEnd) < new Date(v)) setEditEnd(''); }} />
                </div>
                <div>
                  <Label>{t('planned_end', { defaultValue: 'Planned end' })}</Label>
                  <DateTimeField value={editEnd} onChange={setEditEnd} />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
                <Button size="sm" disabled={busy || !editTitle.trim()}
                  onClick={() => {
                    if (editStart && editEnd && new Date(editEnd) < new Date(editStart)) {
                      toast.error(t('planned_window_bad', { defaultValue: 'Planned end must be on or after planned start.' }));
                      return;
                    }
                    run(async () => {
                      await updateWorkItem(w.id, {
                        title: editTitle.trim(), description: editDesc, priority: editPriority,
                        planned_start_at: editStart ? new Date(editStart).toISOString() : undefined,
                        planned_end_at: editEnd ? new Date(editEnd).toISOString() : undefined,
                        // null clears the size ("Not sized"); only sent when it changed.
                        ...((w.effort_size ?? null) !== editEffort ? { effort_size: editEffort } : {}),
                      });
                      setEditing(false);
                    }, t('updated_ok', { defaultValue: 'Details updated.' }) as string);
                  }}>
                  {t('save', { defaultValue: 'Save' })}
                </Button>
              </div>
            </div>
          ) : w.description ? (
            <p className="text-sm text-foreground whitespace-pre-wrap">{w.description}</p>
          ) : canEdit ? (
            <button type="button" className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
              onClick={() => { setEditTitle(w.title); setEditDesc(''); setEditPriority(w.priority); setEditStart(isoToLocalInput(w.planned_start_at)); setEditEnd(isoToLocalInput(w.planned_end_at)); setEditEffort(w.effort_size ?? null); setEditing(true); }}>
              {t('add_desc', { defaultValue: '+ Add a description' })}
            </button>
          ) : null}
          {w.decision_note && (
            <div className="rounded-md bg-warning-soft text-warning-strong text-sm px-3 py-2">
              {t('reviewer_note', { defaultValue: 'Reviewer note:' })} {w.decision_note}
            </div>
          )}

          {/* Assets & attachments — the primary work workspace (files/links,
              grouped reference/deliverable/supporting, versioned). */}
          <div className="rounded-lg border border-border bg-card p-4">
            <WorkAssetsSection
              workItemId={w.id}
              workItemTitle={w.title}
              canEdit={canEdit && WORKABLE.includes(w.status)}
              defaultPurpose={mode === 'actor' ? 'deliverable' : 'reference'}
              onChange={load}
            />
          </div>

          {/* Sub-items live in the main column: the breakdown IS the work, and
              titles need room (the rail truncated them). Rows wrap on narrow screens. */}
          <div className="rounded-lg border border-border p-4 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className={secTitle}>{t('subitems', { defaultValue: 'Sub-items' })}</span>
              {/* Completion policy (design §6): manual (owner submits when done)
                  or require_subitems (the server blocks submission until every
                  sub-item is completed or cancelled). Frozen once submitted,
                  like every other definition field. */}
              {canSetCompletionPolicy && (
                <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  {t('completion_policy', { defaultValue: 'Completion' })}
                  <select
                    aria-label={t('completion_policy_aria', { defaultValue: 'Completion policy' }) as string}
                    data-testid="completion-policy"
                    className="rounded-md border border-border bg-background text-foreground text-xs px-1.5 py-1"
                    value={completionPolicy} disabled={busy || !canEdit}
                    onChange={(e) => {
                      const next = e.target.value as CompletionPolicy;
                      if (next === completionPolicy) return;
                      run(() => updateWorkItem(w.id, { completion_policy: next }),
                        t('completion_policy_saved', { defaultValue: 'Completion policy updated.' }) as string);
                    }}>
                    <option value="manual">{t('completion_manual', { defaultValue: 'Manual' })}</option>
                    <option value="require_subitems">{t('completion_require_subitems', { defaultValue: 'Require all subtasks' })}</option>
                  </select>
                </label>
              )}
            </div>
            {subTotal > 0 && (
              <div className="space-y-1" data-testid="subtask-progress">
                <p className="text-xs text-muted-foreground tabular-nums">
                  {t('subtask_progress', { defaultValue: '{{done}} / {{total}} subtasks · {{progress}}%', done: subDoneCount, total: subTotal, progress: subProgress })}
                </p>
                <div className="h-1 w-full rounded-full bg-muted overflow-hidden" role="progressbar"
                  aria-valuenow={subProgress} aria-valuemin={0} aria-valuemax={100}>
                  <div className="h-full rounded-full bg-success" style={{ width: `${subProgress}%` }} />
                </div>
              </div>
            )}
            {children.map((c) => {
              const unassigned = !c.owner_employee_id;
              // Draft/planned/reopened without an owner → initial assign; an
              // active owned sub-item → reassign (tenant policy permitting).
              const showAssign = canAssignSubtasks && canShowAssign(c.status, c.owner_employee_id, true);
              const showReassign = canAssignSubtasks && workPolicy?.allow_reassignment !== false
                && canReassign(c.status, c.owner_employee_id, true);
              return (
                <div key={c.id} data-testid={`subtask-row-${c.id}`}
                  className="flex w-full flex-wrap items-start justify-between gap-x-3 gap-y-1 py-1.5 px-1 hover:bg-muted/50 rounded">
                  <button type="button" className="min-w-0 flex-1 basis-48 text-left text-sm" onClick={() => openItem(c.id)}>
                    <span className="text-foreground block break-words">{c.title}</span>
                    <span className={`text-[11px] block break-words ${unassigned ? 'text-warning-strong' : 'text-muted-foreground'}`}>
                      {unassigned
                        ? t('subtask_unassigned', { defaultValue: 'Unassigned' })
                        : (c.owner_name || nameOf(c.owner_employee_id))}
                    </span>
                  </button>
                  <span className="inline-flex items-center gap-2 shrink-0 self-center">
                    {(showAssign || showReassign) && (
                      <button type="button" disabled={busy}
                        className="text-[11px] text-primary-600 hover:underline inline-flex items-center gap-0.5"
                        aria-label={(showAssign
                          ? t('assign_subtask_aria', { defaultValue: 'Assign subtask {{title}}', title: c.title })
                          : t('reassign_subtask_aria', { defaultValue: 'Reassign subtask {{title}}', title: c.title })) as string}
                        onClick={() => setSubAssign(c)}>
                        {showAssign ? <UserCheck className="w-3 h-3" /> : <ArrowRightLeft className="w-3 h-3" />}
                        {showAssign ? t('assign', { defaultValue: 'Assign' }) : t('reassign', { defaultValue: 'Reassign' })}
                      </button>
                    )}
                    <WorkStatusBadge status={c.status} />
                  </span>
                </div>
              );
            })}
            <ReassignDialog open={!!subAssign} subtask
              mode={subAssign && !subAssign.owner_employee_id ? 'assign' : 'reassign'}
              workItemId={subAssign?.id || ''}
              currentOwnerId={subAssign?.owner_employee_id}
              currentOwnerName={subAssign?.owner_employee_id ? (subAssign.owner_name || nameOf(subAssign.owner_employee_id)) : undefined}
              fetchPage={fetchSubtaskAssignees}
              onClose={() => setSubAssign(null)}
              onDone={() => { load(); onChanged(); }} />
            {children.length === 0 && !canEdit && (
              <p className="text-xs text-muted-foreground">{t('no_subitems', { defaultValue: 'No sub-items.' })}</p>
            )}
            {canEdit && (
              <div className="space-y-2 pt-1">
                <div className="flex gap-2">
                  <Input value={subTitle} onChange={(e) => setSubTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && subTitle.trim() && !busy) {
                        e.preventDefault();
                        run(async () => {
                          if (subOwner) rememberPersonName(subOwner.employee_id, subOwner.display_name);
                          await createWorkItem({ title: subTitle.trim(), parent_id: w.id, project_id: w.project_id, owner_employee_id: subOwner?.employee_id });
                          setSubTitle(''); setSubOwner(null);
                        }, t('subitem_ok', { defaultValue: 'Sub-item added.' }) as string);
                      }
                    }}
                    placeholder={t('subitem_ph', { defaultValue: 'Break it down — add a sub-item…' }) as string} />
                  <Button size="sm" variant="outline" disabled={busy || !subTitle.trim()}
                    onClick={() => run(async () => {
                      if (subOwner) rememberPersonName(subOwner.employee_id, subOwner.display_name);
                      await createWorkItem({ title: subTitle.trim(), parent_id: w.id, project_id: w.project_id, owner_employee_id: subOwner?.employee_id });
                      setSubTitle(''); setSubOwner(null);
                    }, t('subitem_ok', { defaultValue: 'Sub-item added.' }) as string)}>
                    {t('add', { defaultValue: 'Add' })}
                  </Button>
                </div>
                {canAssign && (
                  <PeoplePicker id={`subowner-${w.id}`} size="sm" value={subOwner} onChange={setSubOwner}
                    fetchPage={fetchSubtaskOwners} disabled={busy}
                    placeholder={t('subitem_owner_default', { defaultValue: 'Owner: same as parent' }) as string}
                    emptyHint={t('subitem_owner_hint', { defaultValue: 'Leave empty to keep the parent\'s owner.' }) as string} />
                )}
              </div>
            )}
          </div>

          {checklist.length > 0 && (
            <div>
              <Label>{t('checklist', { defaultValue: 'Checklist' })}</Label>
              <div className="mt-1 space-y-1">
                {checklist.map((c) => {
                  const toggleable = mode === 'actor' && WORKABLE.includes(w.status) && !busy;
                  return (
                    <button key={c.id} type="button" disabled={!toggleable}
                      className="flex w-full items-center gap-2 text-left text-sm py-1 disabled:cursor-default"
                      onClick={() => toggleable && run(
                        () => toggleChecklist(w.id, c.id, !c.done),
                        c.done
                          ? t('unchecked', { defaultValue: 'Checklist item reopened.' }) as string
                          : t('checked', { defaultValue: 'Checklist item done.' }) as string,
                      )}>
                      {c.done
                        ? <CheckCircle2 className="w-4 h-4 text-success-strong shrink-0" />
                        : <Circle className="w-4 h-4 text-muted-foreground shrink-0" />}
                      <span className={c.done ? 'text-muted-foreground line-through' : 'text-foreground'}>{c.label}</span>
                      {c.required && !c.done && (
                        <span className="ml-auto text-xs text-warning-strong">{t('required', { defaultValue: 'required' })}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {startReqs.length > 0 && (
            <div>
              <Label>{t('start_reqs', { defaultValue: 'Start evidence (capture when work begins)' })}</Label>
              <div className="mt-1 space-y-1">
                {startReqs.map((r) => {
                  const ok = reqSatisfied(r, evidence);
                  return (
                    <div key={r.id} className="flex items-center gap-2 text-sm py-1">
                      {ok
                        ? <CheckCircle2 className="w-4 h-4 text-success-strong shrink-0" />
                        : <Circle className="w-4 h-4 text-muted-foreground shrink-0" />}
                      <span className="text-foreground">{r.label}</span>
                      <span className="text-xs text-muted-foreground">({r.kind})</span>
                      {r.required && !ok && (
                        <span className="ml-auto text-xs text-warning-strong">{t('required', { defaultValue: 'required' })}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {completionReqs.length > 0 && (
            <div>
              <Label>{t('evidence_reqs', { defaultValue: 'Completion evidence (required before submit)' })}</Label>
              <div className="mt-1 space-y-1">
                {completionReqs.map((r) => {
                  const ok = reqSatisfied(r, evidence);
                  return (
                    <div key={r.id} className="flex items-center gap-2 text-sm py-1">
                      {ok
                        ? <CheckCircle2 className="w-4 h-4 text-success-strong shrink-0" />
                        : <Circle className="w-4 h-4 text-muted-foreground shrink-0" />}
                      <span className="text-foreground">{r.label}</span>
                      <span className="text-xs text-muted-foreground">({r.kind})</span>
                      {r.required && !ok && (
                        <span className="ml-auto text-xs text-warning-strong">{t('required', { defaultValue: 'required' })}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {evidence.length > 0 && (
            <div>
              <Label>{t('evidence', { defaultValue: 'Evidence' })}</Label>
              <div className="mt-1 space-y-1">
                {evidence.map((e) => (
                  <div key={e.id} className="flex items-start gap-2 text-sm text-foreground">
                    <span className="pt-0.5 text-xs text-muted-foreground tabular-nums shrink-0">{fmtDT(e.created_at)}</span>
                    {/* Notes are the evidence itself — show all of it, never truncate. */}
                    {e.kind === 'note' ? (
                      <span className="min-w-0 whitespace-pre-wrap break-words">{e.note || '—'}</span>
                    ) : (
                      <>
                        <span className="min-w-0 truncate">{e.file_name || e.kind}{e.caption ? ` — ${e.caption}` : ''}</span>
                        {e.storage_key && (
                          <button type="button"
                            className="ml-auto inline-flex items-center gap-1 text-xs text-primary-500 hover:underline shrink-0"
                            onClick={() => openEvidence(w.id, e.id, e.file_name)
                              .catch((err) => toast.error(getApiErrorMessage(err, t('download_failed', { defaultValue: 'Could not open the file.' }))))}>
                            <Download className="w-3.5 h-3.5" />{t('open_file', { defaultValue: 'Open' })}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {mode === 'actor' && WORKABLE.includes(w.status) && (
            <div className="space-y-2">
              <Label>{t('upload', { defaultValue: 'Attach a file (photo or PDF, max 10 MB)' })}</Label>
              {reqs.filter((r) => (r.kind === 'photo' || r.kind === 'document') && !reqSatisfied(r, evidence)).length > 0 && (
                <select className="w-full rounded-md border border-border bg-background text-foreground text-sm px-2.5 py-2"
                  value={fileFor} onChange={(e) => setFileFor(e.target.value)}>
                  <option value="">{t('free_file', { defaultValue: 'Not for a specific requirement' })}</option>
                  {reqs.filter((r) => r.kind === 'photo' || r.kind === 'document').map((r) => (
                    <option key={r.id} value={r.req_key}>{r.label}</option>
                  ))}
                </select>
              )}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                disabled={busy}
                className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-background file:px-3 file:py-1.5 file:text-sm file:text-foreground"
                onChange={(ev) => {
                  const f = ev.target.files?.[0];
                  if (!f) return;
                  ev.target.value = '';
                  run(async () => {
                    await uploadEvidence(w.id, f, { requirement_key: fileFor || undefined });
                    setFileFor('');
                  }, t('uploaded', { defaultValue: 'Evidence uploaded.' }) as string);
                }} />
            </div>
          )}

          {mode === 'actor' && WORKABLE.includes(w.status) && (
            <div className="space-y-2">
              <Label>{t('add_note', { defaultValue: 'Add note evidence' })}</Label>
              {reqs.filter((r) => r.kind === 'note' && !reqSatisfied(r, evidence)).length > 0 && (
                <select className="w-full rounded-md border border-border bg-background text-foreground text-sm px-2.5 py-2"
                  value={evFor} onChange={(e) => setEvFor(e.target.value)}>
                  <option value="">{t('free_note', { defaultValue: 'Free-standing note' })}</option>
                  {reqs.filter((r) => r.kind === 'note').map((r) => (
                    <option key={r.id} value={r.req_key}>{r.label}</option>
                  ))}
                </select>
              )}
              <div className="flex gap-2">
                <Textarea rows={2} value={evNote} onChange={(e) => setEvNote(e.target.value)}
                  placeholder={t('note_ph', { defaultValue: 'What was done, observed, or delivered…' }) as string} />
                <Button size="sm" variant="outline" disabled={busy || !evNote.trim()}
                  onClick={() => run(async () => {
                    await addNoteEvidence(w.id, { note: evNote.trim(), requirement_key: evFor || undefined });
                    setEvNote(''); setEvFor('');
                  }, t('note_added', { defaultValue: 'Evidence noted.' }) as string)}>
                  {t('add', { defaultValue: 'Add' })}
                </Button>
              </div>
            </div>
          )}

          {mode === 'actor' && WORKABLE.includes(w.status) &&
            reqs.some((r) => r.kind === 'acknowledgement' && !reqSatisfied(r, evidence)) && (
            <div className="space-y-2">
              <Label>{t('ack_req', { defaultValue: 'Client acknowledgement — send a confirmation link' })}</Label>
              {ackLink ? (
                <div className="flex items-center gap-2">
                  <Input readOnly value={ackLink} onFocus={(e) => e.target.select()} />
                  <Button size="sm" variant="outline"
                    onClick={() => { navigator.clipboard?.writeText(ackLink); toast.success(t('ack_copied', { defaultValue: 'Link copied — share it with your client.' }) as string); }}>
                    {t('copy', { defaultValue: 'Copy' })}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder={t('ack_name_ph', { defaultValue: "Client's name" }) as string}
                    value={ackName} onChange={(e) => setAckName(e.target.value)} />
                  <Input placeholder={t('ack_email_ph', { defaultValue: 'Email (optional)' }) as string}
                    value={ackEmail} onChange={(e) => setAckEmail(e.target.value)} />
                  <Button size="sm" variant="outline" className="col-span-2"
                    disabled={busy || !ackName.trim()}
                    onClick={() => run(async () => {
                      const reqKey = ackFor ||
                        reqs.find((r) => r.kind === 'acknowledgement' && !reqSatisfied(r, evidence))?.req_key || '';
                      const { token } = await createAckInvite(w.id, {
                        name: ackName.trim(), email: ackEmail.trim() || undefined,
                        requirement_key: reqKey || undefined,
                      });
                      setAckLink(`${window.location.origin}/ack/${token}`);
                    }, t('ack_created', { defaultValue: 'Acknowledgement link created — copy and share it.' }) as string)}>
                    {t('ack_request', { defaultValue: 'Create acknowledgement link' })}
                  </Button>
                </div>
              )}
            </div>
          )}

          {(detail?.execution_links || []).length > 0 && (
            <div className="rounded-md bg-accent text-accent-foreground px-3 py-2 text-sm">
              {t('exec_linked', { defaultValue: 'Field execution logged' })}
              {mode === 'actor' && (
                <>
                  {' · '}
                  <Link to="../my-assignments" className="text-primary-500 hover:underline">
                    {t('exec_checkin', { defaultValue: 'check in from My Assignments' })}
                  </Link>
                </>
              )}
            </div>
          )}

          {mode === 'actor' && WORKABLE.includes(w.status) && (detail?.execution_links || []).length === 0 && execTypes.length > 0 && (
            <div className="space-y-2">
              <Label>{t('exec_log', { defaultValue: 'Log field execution (check-in / GPS / presence via My Assignments)' })}</Label>
              <div className="flex gap-2">
                <select className="w-full rounded-md border border-border bg-background text-foreground text-sm px-2.5 py-2"
                  value={execType} onChange={(e) => setExecType(e.target.value)}>
                  {execTypes.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                </select>
                <Button size="sm" variant="outline" disabled={busy || !execType}
                  onClick={() => run(async () => {
                    const me: any = await getMyProfile();
                    const chosen = execTypes.find((x) => x.key === execType);
                    const a = await createAssignment({
                      employeeId: me?.id, typeKey: execType, title: w.title,
                      description: w.description || (t('exec_desc', { defaultValue: 'Field execution of a work item' }) as string),
                      // Field-execution types (client site / field / travel) require a
                      // planned start. "Log" means "I'm doing this now", so default to the
                      // work item's planned start when scheduled, otherwise now — matching
                      // the remote_work path, which needs no time.
                      plannedStartAt: w.planned_start_at || new Date().toISOString(),
                      // Only carry the end when the item was scheduled with a start too,
                      // so a defaulted "now" start can't land after a past planned end.
                      ...(w.planned_start_at && w.planned_end_at ? { plannedEndAt: w.planned_end_at } : {}),
                      // The linked work item already exists — the bridge must
                      // NOT auto-create a duplicate.
                      workItemId: w.id,
                    });
                    await linkExecution(w.id, a.id);
                    if (chosen?.requiresApproval) {
                      toast(t('exec_pending', { defaultValue: 'The assignment awaits approval before check-in.' }) as string);
                    }
                  }, t('exec_ok', { defaultValue: 'Field execution logged — check in from My Assignments.' }) as string)}>
                  {t('exec_btn', { defaultValue: 'Log' })}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ---- Right rail: team, actions, discussion, timeline ---- */}
        <div className="space-y-4">
          {/* W14 follow-up: no Team card → the assignee still sees why Hand back
              is unavailable after starting (the card is the only other place). */}
          {!teamCardShown && handBackLocked && (
            <div className="rounded-lg border border-border p-4 space-y-2" data-testid="hand-back-locked-card">
              <span className={`${secTitle} inline-flex items-center gap-1.5`}>
                <UserCheck className="w-3.5 h-3.5" />
                {t('assignee', { defaultValue: 'Assignee' })}
              </span>
              <HandBackLocked workItemId={w.id} />
            </div>
          )}
          {/* Team: ONE accountable owner + contributors + individually-owned
              sub-items — never a shared completion. Contribution is derived
              per person (sub-items, effort, evidence). */}
          {teamCardShown && (
            <div className="rounded-lg border border-border p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className={`${secTitle} inline-flex items-center gap-1.5`}>
                  <Users className="w-3.5 h-3.5" />
                  {t('team', { defaultValue: 'Team' })}
                </span>
                <Badge variant="outline" className="text-[10px]">
                  {w.work_type === 'team'
                    ? t('type_team', { defaultValue: 'Team work' })
                    : t('type_individual', { defaultValue: 'Individual' })}
                </Badge>
              </div>

              {/* Assignee / Owner — the ONE accountable person. Distinct from
                  contributors: adding a contributor never assigns the work. A Draft
                  with no owner needs an explicit assignment before it can start. */}
              <div className="space-y-1.5 border-b border-border pb-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-muted-foreground">{t('assignee', { defaultValue: 'Assignee' })}</span>
                  {w.owner_employee_id
                    ? (
                      <span className="inline-flex items-center gap-1.5 min-w-0">
                        <span className="text-sm font-medium text-foreground truncate">{w.owner_name || nameOf(w.owner_employee_id)}</span>
                        <RoleChip role="owner" />
                        {role.isOwner && <span className="text-[10px] text-muted-foreground">({t('you', { defaultValue: 'you' })})</span>}
                      </span>
                    )
                    : <span className="text-sm text-muted-foreground">{t('not_assigned', { defaultValue: 'Not assigned' })}</span>}
                </div>
                {canShowAssign(w.status, w.owner_employee_id, canAssign) && (
                  <>
                    <p className="text-[11px] text-muted-foreground">
                      {t('draft_assign_hint', { defaultValue: 'This work is still a draft. Select an assignee and assign it when ready — adding contributors does not assign it.' })}
                    </p>
                    <div className="space-y-1.5">
                      <PeoplePicker id={`assign-${w.id}`} size="sm" value={assignee} onChange={setAssignee}
                        fetchPage={fetchAssignees} disabled={busy}
                        placeholder={t('select_assignee', { defaultValue: 'Select an employee…' }) as string} />
                      <Button size="sm" disabled={busy || !assignee}
                        onClick={() => run(async () => {
                          if (!assignee) return;
                          rememberPersonName(assignee.employee_id, assignee.display_name);
                          await assignWorkItem(w.id, assignee.employee_id, assignee.user_id || undefined);
                          setAssignee(null);
                        }, t('assigned_ok', { defaultValue: 'Work assigned.' }) as string)}>
                        <UserCheck className="w-4 h-4 mr-1" />{t('assign_work', { defaultValue: 'Assign Work' })}
                      </Button>
                    </div>
                    {!assignee && (
                      <p className="text-[11px] text-muted-foreground">{t('assignee_required', { defaultValue: 'Select an assignee before assigning this work.' })}</p>
                    )}
                  </>
                )}
                {/* Reassign an already-owned, active item to a different owner (keeps
                    its lifecycle status; both owners are notified). Opens a small
                    dialog that calls POST /reassign with an optional reason. Hidden
                    when the tenant policy disables reassignment or I cannot assign. */}
                {canReassignItem && (
                  <Button type="button" size="sm" variant="outline" className="w-full"
                    disabled={busy} onClick={() => setReassignOpen(true)}>
                    <ArrowRightLeft className="w-4 h-4 mr-1" />
                    {t('reassign', { defaultValue: 'Reassign' })}
                  </Button>
                )}
                {viewer?.can_unassign && (
                  <Button type="button" size="sm" variant="ghost" className="w-full text-muted-foreground"
                    disabled={busy} onClick={() => setUnassignOpen(true)}>
                    <UserMinus className="w-4 h-4 mr-1" />
                    {role.isOwner
                      ? t('hand_back', { defaultValue: 'Hand back' })
                      : t('unassign', { defaultValue: 'Remove assignment' })}
                  </Button>
                )}
                {handBackLocked && <HandBackLocked workItemId={w.id} />}
                <UnassignWorkDialog open={unassignOpen} workItemId={w.id}
                  ownerName={w.owner_employee_id ? (w.owner_name || nameOf(w.owner_employee_id)) : undefined}
                  selfHandBack={role.isOwner} hasOpenSubitems={openSubitems > 0} expectedUpdatedAt={w.updated_at}
                  onClose={() => setUnassignOpen(false)}
                  onDone={() => { load(); onChanged(); }} />
                <ReassignDialog open={reassignOpen} workItemId={w.id}
                  currentOwnerId={w.owner_employee_id} currentOwnerName={w.owner_employee_id ? (w.owner_name || nameOf(w.owner_employee_id)) : undefined}
                  hasOpenSubitems={openSubitems > 0} expectedUpdatedAt={w.updated_at}
                  fetchPage={fetchAssignees}
                  onClose={() => setReassignOpen(false)}
                  onDone={() => { load(); onChanged(); }} />
              </div>

              <span className="text-xs font-medium text-muted-foreground">{t('collaborators_label', { defaultValue: 'Collaborators' })}</span>
              <div className="flex flex-wrap gap-1.5" data-testid="work-team-people">
                {contributorRows.map((c) => {
                  const isOwner = !!w.owner_employee_id && c.employee_id === w.owner_employee_id;
                  const isMe = !!myEmployeeId && c.employee_id === myEmployeeId;
                  return (
                    <span key={c.employee_id}
                      className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium ${isOwner ? 'bg-orange-100 text-orange-700 dark:bg-orange-500/[0.16] dark:text-orange-300' : 'bg-muted text-foreground'}`}>
                      {contribName(c)}
                      <RoleChip role={isOwner ? 'owner' : 'collaborator'} />
                      {isMe && <span className="text-[10px] opacity-80">({t('you', { defaultValue: 'you' })})</span>}
                      {canAddCollaborators && !isOwner && (
                        <button type="button" aria-label={t('remove_collaborator', { defaultValue: 'Remove collaborator' }) as string} className="hover:text-red-500"
                          disabled={busy}
                          onClick={() => run(async () => { await removeWorkContributor(w.id, c.employee_id); },
                            t('contrib_removed', { defaultValue: 'Contributor removed.' }) as string)}>
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  );
                })}
                {contributorRows.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t('no_contribs', { defaultValue: 'No collaborators yet.' })}</p>
                )}
              </div>
              {/* Add collaborator: work:assign holders, or the owner/creator when the
                  server says the tenant policy allows it (no work:assign needed).
                  Eligible people are scoped server-side (company-wide only when
                  cross-department collaboration is on). */}
              {canAddCollaborators && !['completed', 'cancelled'].includes(w.status) && (
                <div className="space-y-1.5 pt-1" data-testid="add-collaborator">
                  <Label htmlFor={`contrib-${w.id}`} className="text-xs">{t('add_collaborator', { defaultValue: 'Add collaborator' })}</Label>
                  <PeoplePicker id={`contrib-${w.id}`} size="sm" value={newContributor} onChange={setNewContributor}
                    fetchPage={fetchCollaborators} disabled={busy}
                    exclude={[...contributorRows.map((c) => c.employee_id), ...(w.owner_employee_id ? [w.owner_employee_id] : [])]}
                    policyDisabledMessage={t('picker_policy_disabled_collab', { defaultValue: "Your company hasn't enabled collaborators" }) as string}
                    placeholder={t('add_collaborator_ph', { defaultValue: 'Add a collaborator…' }) as string} />
                  {newContributor && (
                    <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => run(async () => {
                        rememberPersonName(newContributor.employee_id, newContributor.display_name);
                        await addWorkContributor(w.id, newContributor.employee_id);
                        setNewContributor(null);
                      }, t('contrib_added', { defaultValue: 'Collaborator added — this is now team work.' }) as string)}>
                      {t('add', { defaultValue: 'Add' })}
                    </Button>
                  )}
                </div>
              )}
              {w.work_type === 'team' && (team?.contributions || []).length > 0 && (
                <div className="pt-1">
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    {t('contribution', { defaultValue: 'Individual contribution' })}
                  </p>
                  <div className="space-y-1">
                    {(team?.contributions || []).map((c) => (
                      <div key={c.employee_id} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-foreground truncate">{contribName(c)}{c.is_owner ? ` · ${t('role_owner', { defaultValue: 'Owner' })}` : ''}</span>
                        <span className="text-muted-foreground tabular-nums whitespace-nowrap">
                          {c.subtasks_completed}/{c.subtasks_total} {t('subitems_short', { defaultValue: 'sub-items' })}
                          {c.effort_hours > 0 && ` · ${c.effort_hours}h`}
                          {c.evidence_count > 0 && ` · ${c.evidence_count} ${t('evidence_short', { defaultValue: 'evidence' })}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Actions */}
          <div className="rounded-lg border border-border p-4 space-y-2">
            <span className={secTitle}>{t('actions', { defaultValue: 'Actions' })}</span>
            {mode === 'reviewer' && CAN_REVIEW.includes(w.status) && (
              <>
                <Label>{t('decision_note', { defaultValue: 'Decision note (sent to the employee)' })}</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder={t('decision_ph', { defaultValue: 'Optional for approval; explain what to change otherwise.' }) as string} />
              </>
            )}
            {collabHint && (
              <p className="text-xs text-muted-foreground" data-testid="collab-hint">
                {t('collab_hint', { defaultValue: "You're collaborating — the owner submits this work" })}
              </p>
            )}
            <div className="flex flex-col gap-2">
              {actsAsOwner && CAN_START.includes(w.status) && (
                <Button disabled={busy} onClick={() => run(() => startWork(w.id), t('started_ok', { defaultValue: 'Work started.' }) as string)}>
                  <Play className="w-4 h-4 mr-1" />{t('start', { defaultValue: 'Start' })}
                </Button>
              )}
              {actsAsOwner && CAN_SUBMIT.includes(w.status) && (
                <>
                  {submitBlockedBySub ? (
                    <p className="text-xs text-warning-strong" data-testid="submit-blocked-hint">
                      {t('submit_blocked_subtasks', {
                        defaultValue: 'Completion requires every subtask — {{count}} still open. Submitting is refused until they are completed or cancelled.',
                        count: children.length > 0 ? subOpen : Math.max(0, subTotal - subDoneCount),
                      })}
                    </p>
                  ) : subOpen > 0 && (
                    <p className="text-xs text-warning-strong">
                      {t('sub_open_warn', {
                        defaultValue: '{{count}} sub-item(s) still open — submitting this item does not close them.',
                        count: subOpen,
                      })}
                    </p>
                  )}
                  <Input type="number" min="0" step="0.5" value={effort}
                    onChange={(e) => setEffort(e.target.value)}
                    placeholder={t('effort_ph', { defaultValue: 'Hours (optional)' }) as string} />
                  {/* The SERVER resolved item → work type → company policy into
                      `review_required`; the button only reports its decision.
                      Absent on older backends → review, today's behaviour. */}
                  <p className="text-xs text-muted-foreground" data-testid="completion-route-hint">
                    {reviewRequired
                      ? t('submit_review_hint', { defaultValue: 'This goes to a reviewer — they complete it once they have checked your work.' })
                      : t('mark_complete_hint', { defaultValue: 'You complete this yourself — no reviewer. Required checklist items and evidence still apply.' })}
                  </p>
                  <Button disabled={busy} data-testid="submit-action" onClick={() => run(
                    () => submitWork(w.id, parseFloat(effort) || undefined),
                    (reviewRequired
                      ? t('submitted_ok', { defaultValue: 'Submitted for completion review.' })
                      : t('mark_complete_ok', { defaultValue: 'Work completed.' })) as string,
                  )}>
                    {reviewRequired
                      ? <><Send className="w-4 h-4 mr-1" />{t('submit_for_review', { defaultValue: 'Submit for review' })}</>
                      : <><CheckCircle2 className="w-4 h-4 mr-1" />{t('mark_complete', { defaultValue: 'Mark complete' })}</>}
                  </Button>
                </>
              )}
              {mode === 'reviewer' && CAN_REVIEW.includes(w.status) && (
                <>
                  <Button disabled={busy}
                    onClick={() => run(() => approveWork(w.id, note.trim() || undefined), t('approved_ok', { defaultValue: 'Approved — work item completed.' }) as string)}>
                    <ThumbsUp className="w-4 h-4 mr-1" />{t('approve', { defaultValue: 'Approve' })}
                  </Button>
                  <Button variant="outline" disabled={busy || !note.trim()}
                    onClick={() => run(() => requestChanges(w.id, note.trim()), t('changes_ok', { defaultValue: 'Changes requested.' }) as string)}>
                    <Undo2 className="w-4 h-4 mr-1" />{t('request_changes', { defaultValue: 'Request changes' })}
                  </Button>
                </>
              )}
              {actsAsOwner && ['assigned', 'started', 'in_progress'].includes(w.status) && (
                <Button variant="outline" disabled={busy}
                  onClick={() => run(() => holdWork(w.id), t('held', { defaultValue: 'Work put on hold.' }) as string)}>
                  {t('hold', { defaultValue: 'Hold' })}
                </Button>
              )}
              {/* The accountable owner may cancel their work at ANY level, not just
                  sub-items. Restricting top-level cancellation to reviewers left
                  the person who owns a piece of work unable to stop it — and on an
                  item with no reviewer, unable to stop it at all. The server is the
                  real gate (cancel requires reach, service.go Transition default
                  branch), so a stale view gets a clean refusal rather than a
                  silently missing button. */}
              {canCancelItem && (
                <Button variant="outline" disabled={busy} onClick={() => setCancelOpen(true)}>
                  {t('cancel_item', { defaultValue: 'Cancel item' })}
                </Button>
              )}
              {viewer?.can_remove && (
                <Button variant="ghost" className="text-danger-strong" disabled={busy} onClick={() => setRemoveOpen(true)}>
                  {t('remove', { defaultValue: 'Remove' })}
                </Button>
              )}
              <RemoveWorkDialog open={removeOpen} item={w} subitems={detail?.subitems?.total ?? children.length}
                onClose={() => setRemoveOpen(false)}
                onDone={() => { load(); onChanged(); }} />
              <CancelWorkDialog
                open={cancelOpen}
                busy={busy}
                openSubitems={openSubitems}
                onClose={() => setCancelOpen(false)}
                onConfirm={(opts) => {
                  setCancelOpen(false);
                  void run(() => cancelWork(w.id, { ...opts, expectedUpdatedAt: w.updated_at }),
                    t('cancelled_ok', { defaultValue: 'Work item cancelled.' }) as string);
                }}
              />
              {viewer?.can_stop_series && viewer.series_id && confirm && (
                <Button variant="outline" disabled={busy} onClick={() => void stopSeries()}>
                  {t('stop_series', { defaultValue: 'Stop repeating' })}
                </Button>
              )}
              {mode === 'reviewer' && ['completed', 'cancelled'].includes(w.status) && (
                <Button variant="outline" disabled={busy}
                  onClick={() => run(() => reopenWork(w.id), t('reopened_ok', { defaultValue: 'Work item reopened.' }) as string)}>
                  {t('reopen', { defaultValue: 'Reopen' })}
                </Button>
              )}
            </div>
          </div>

          {/* Discussion: collaboration between assignee and reviewer — distinct
              from formal evidence, open at every lifecycle stage. */}
          <div className="rounded-lg border border-border p-4 space-y-2">
            <span className={secTitle}>
              <MessageSquare className="w-4 h-4 inline mr-1" />
              {t('discussion', { defaultValue: 'Discussion' })}
            </span>
            {/* Dana's contextual recognition suggestions for this work item
                (peer-first, human-confirmed). Silent when there are none. */}
            <RecognitionSuggestions
              workItemId={w.id}
              refreshKey={comments.length}
              nameByUserId={Object.fromEntries(
                comments
                  .filter((c) => c.actor_user_id && c.actor_name)
                  .map((c) => [c.actor_user_id as string, c.actor_name as string]),
              )}
            />
            {comments.length === 0 && (
              <p className="text-xs text-muted-foreground">{t('no_comments', { defaultValue: 'No comments yet.' })}</p>
            )}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {comments.map((a) => (
                <div key={a.id} className="rounded-md bg-muted/50 px-2.5 py-1.5">
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {a.actor_name ? <span className="font-medium text-foreground">{a.actor_name}</span> : null}
                    {a.actor_name ? ' · ' : ''}{fmtDT(a.occurred_at)}
                  </p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{a.summary}</p>
                  {!!a.attachment_asset_ids?.length && (
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      📎 {t('note_with_attachments', { defaultValue: 'Sent with {{count}} attachment(s)', count: a.attachment_asset_ids.length })}
                    </p>
                  )}
                  {a.actor_user_id && a.actor_user_id !== myUserId && (
                    <div className="mt-1">
                      <RecognizeInline
                        recipientUserId={a.actor_user_id}
                        recipientName={a.actor_name}
                        sourceWorkItemId={w.id}
                        sourceCommentId={a.id}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder={t('comment_ph', { defaultValue: 'Write a comment…' }) as string} />
              <Button size="sm" variant="outline" disabled={busy || !comment.trim()}
                onClick={() => run(async () => {
                  await addWorkComment(w.id, comment.trim());
                  setComment('');
                }, t('comment_ok', { defaultValue: 'Comment posted.' }) as string)}>
                {t('send', { defaultValue: 'Send' })}
              </Button>
            </div>
          </div>

          {/* Assignment history (design §6): who moved the owner, from whom, why. */}
          <AssignmentHistorySection history={detail?.assignment_history} />

          {timeline.length > 0 && (
            <div className="rounded-lg border border-border p-4 space-y-1">
              <span className={secTitle}>{t('timeline', { defaultValue: 'Timeline' })}</span>
              {timeline.map((a) => (
                <p key={a.id} className="text-xs text-muted-foreground tabular-nums">
                  {fmtDT(a.occurred_at)}
                  {a.actor_name ? <> · <span className="font-medium text-foreground">{a.actor_name}</span></> : ''}
                  {' · '}{a.summary || a.verb}
                  {timelineCanOpen(a) && (
                    <>
                      {' · '}
                      <button type="button" className="font-medium text-primary-600 hover:underline"
                        onClick={() => void openTimelineAsset(w.id, a)}>
                        {t('open', { defaultValue: 'Open' })}
                      </button>
                    </>
                  )}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// A timeline file row can be opened when it names an uploaded file version.
// "Removed" rows can't (the file left the workspace); link versions open from
// the asset card. Access is re-checked by the server on every open.
export const timelineCanOpen = (a: WorkActivity): boolean =>
  !!a.asset_id && !!a.asset_version_id && !a.asset_removed && (a.verb === 'asset_added' || a.verb === 'asset_version_added') && / — file$/.test(a.summary || '');

async function openTimelineAsset(workItemId: string, a: WorkActivity) {
  try {
    await downloadWorkAssetVersion(workItemId, a.asset_id!, a.asset_version_id!);
  } catch (e) {
    toast.error(getApiErrorMessage(e, i18n.t('work:asset_download_failed', { defaultValue: 'Could not download.' }) as string));
  }
}

// ---- line-format helpers shared by the create + template dialogs ----
// Evidence: one requirement per line, optional "photo: / document: / note:"
// prefix (no prefix → photo). Checklist: one required step per line.
export const parseEvidenceLines = (text: string) =>
  text.split('\n').map((l) => l.trim()).filter(Boolean).map((line, i) => {
    const m = line.match(/^(photo|document|note)\s*:\s*(.+)$/i);
    return {
      req_key: `completion_${i + 1}`,
      label: (m ? m[2] : line).trim(),
      phase: 'completion',
      kind: m ? m[1].toLowerCase() : 'photo',
      required: true,
    };
  });
export const evidenceToLines = (ev: Array<{ kind?: string; label: string }>) =>
  ev.map((e) => `${e.kind || 'photo'}: ${e.label}`).join('\n');
export const parseChecklistLines = (text: string) =>
  text.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => ({ label: l, required: true }));
export const checklistToLines = (cl: Array<{ label: string }>) => cl.map((c) => c.label).join('\n');

/** Create dialog: work item + checklist + completion evidence + assignee +
 * collaborators. Anyone the policy lets create (`me.can_create`) uses it; the
 * assignee/collaborator pickers are server-scoped per purpose. */
export const CreateWorkDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}> = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation('work');
  const [projects, setProjects] = useState<WorkProject[]>([]);
  const [templates, setTemplates] = useState<WorkTemplate[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [form, setForm] = useState<Record<string, string>>({});
  // The ONE accountable owner (null = "Assign later", the item stays a draft)
  // and optional collaborators (added after create, best-effort).
  const [assignee, setAssignee] = useState<Person | null>(null);
  const [collaborators, setCollaborators] = useState<Person[]>([]);
  const { policy } = useWorkPolicy();
  const projectCtx = form.projectId && form.projectId !== '__new__' ? form.projectId : undefined;
  const withWorkload = !!policy?.workload_enabled;
  const fetchAssignees = useEligibleAssignees('assign', { projectId: projectCtx, withWorkload });
  const fetchCollaborators = useEligibleAssignees('collaborate', { projectId: projectCtx, withWorkload });
  const [checklistText, setChecklistText] = useState('');
  const [evidenceText, setEvidenceText] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [busy, setBusy] = useState(false);
  // Single flight (W24): `busy` is React state, so two clicks in the same tick
  // both saw it false and created the item — or the repeating series — twice.
  const inFlight = useRef(false);
  // Staged assets (files/links) attached to the item AFTER it is created. The
  // evidence/checklist textareas are gone from the default flow — a picked
  // template still seeds them (server-side + the retained state below).
  const [pendingAssets, setPendingAssets] = useState<PendingAsset[]>([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkName, setLinkName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkPurpose, setLinkPurpose] = useState<AssetPurpose>('reference');
  useEffect(() => {
    if (!open) return;
    listProjects({ limit: 100 }).then((p) => setProjects(p.items)).catch(() => undefined);
    // A repeating series is stored as a template, but it is an implementation
    // detail of "New work item + Repeat" — never a Work type to pick (W24).
    listWorkTemplates().then((ts) => setTemplates(ts.filter((tp) => !tp.recurrence_rule))).catch(() => undefined);
  }, [open]);
  const stageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setPendingAssets((p) => [...p, { kind: 'file', name: file.name, file, purpose: 'reference' }]);
    e.target.value = '';
  };
  const stageLink = () => {
    const url = linkUrl.trim();
    if (!/^https?:\/\/.+/i.test(url)) {
      toast.error(t('asset_url_invalid', { defaultValue: 'Enter an http(s) link.' }));
      return;
    }
    setPendingAssets((p) => [...p, { kind: 'link', name: (linkName.trim() || url), url, purpose: linkPurpose }]);
    setLinkName(''); setLinkUrl(''); setLinkPurpose('reference'); setLinkOpen(false);
  };
  // Attach staged assets to a freshly-created item (best-effort; one failure
  // never blocks the others or the create).
  const flushAssets = async (workItemId: string) => {
    for (const a of pendingAssets) {
      try {
        if (a.kind === 'link') await addWorkAssetLink(workItemId, { name: a.name, url: a.url, purpose: a.purpose });
        // Staged at creation: part of the new item (the assignee is told about the
        // item itself), so these don't each send an "attachment added" notice.
        else await uploadWorkAsset(workItemId, a.file, { name: a.name, purpose: a.purpose, notify: false });
      } catch {
        toast.error(t('asset_attach_failed', { defaultValue: 'Could not attach {{name}}.', name: a.name }) as string);
      }
    }
  };
  if (!open) return null;
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const sel = 'w-full rounded-md border border-border bg-background text-foreground text-sm px-2.5 py-2';

  // "Contributes to goal": the ASSIGNEE's open individual goals. Evidence-only —
  // completed work shows on the goal as connected work, never auto-progress.
  const loadGoals = (employeeId: string) => {
    if (!employeeId) { setGoals([]); return; }
    listGoals(employeeId)
      .then((gs) => setGoals(gs.filter((g) => !['completed', 'archived'].includes(g.status))))
      .catch(() => setGoals([]));
  };

  // Picking a work type prefills the pack WYSIWYG — the manager can still tweak
  // every line before creating; template_id is kept for provenance.
  const applyTemplate = (id: string) => {
    setForm((f) => ({ ...f, templateId: id }));
    const tpl = templates.find((x) => x.id === id);
    if (!tpl) return;
    // The template's default effort seeds the Effort select (still editable).
    setForm((f) => ({ ...f, templateId: id, priority: tpl.priority, ...(tpl.default_effort_size ? { effortSize: tpl.default_effort_size } : {}) }));
    setChecklistText(checklistToLines(tpl.checklist));
    setEvidenceText(evidenceToLines(tpl.evidence));
  };

  const submit = async () => {
    if (!form.title?.trim()) {
      toast.error(t('title_required', { defaultValue: 'A title is required.' }));
      return;
    }
    if (form.projectId === '__new__' && !newProjectName.trim()) {
      toast.error(t('project_name_required', { defaultValue: 'Give the new project a name.' }));
      return;
    }
    if (form.plannedStart && form.plannedEnd && new Date(form.plannedEnd) < new Date(form.plannedStart)) {
      toast.error(t('planned_window_bad', { defaultValue: 'Planned end must be on or after planned start.' }));
      return;
    }
    // Repeat → this becomes a recurring series (planned start = the series anchor).
    if (form.repeat) {
      if (!form.plannedStart) {
        toast.error(t('repeat_needs_start', { defaultValue: 'A repeating task needs a Planned start (its first occurrence).' }));
        return;
      }
      if (form.repeat === 'weekly' && !(form.weekdays || '').split(',').filter(Boolean).length) {
        toast.error(t('tpl_pick_weekday', { defaultValue: 'Pick at least one weekday for a weekly repeat.' }));
        return;
      }
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      let projectId = form.projectId && form.projectId !== '__new__' ? form.projectId : undefined;
      if (form.projectId === '__new__') {
        const p = await createProject({ name: newProjectName.trim() });
        projectId = p.id;
      }
      const checklist = parseChecklistLines(checklistText);
      const evidence = parseEvidenceLines(evidenceText);

      if (form.repeat) {
        // Recurring series (a recurring template that auto-spawns tasks).
        const freq = form.repeat as 'daily' | 'weekly' | 'monthly';
        const rule = {
          freq,
          interval: Math.max(1, parseInt(form.interval || '1', 10) || 1),
          ...(freq === 'weekly' ? { weekdays: (form.weekdays || '').split(',').filter(Boolean).map(Number) } : {}),
          ...(freq === 'monthly' ? { by_monthday: parseInt(form.byMonthday || '0', 10) || 0 } : {}),
        };
        const durationMin = form.plannedStart && form.plannedEnd
          ? Math.max(0, Math.round((new Date(form.plannedEnd).getTime() - new Date(form.plannedStart).getTime()) / 60000))
          : undefined;
        const res = await createRecurringWorkItem({
          title: form.title.trim(),
          description: form.description || undefined,
          priority: form.priority || undefined,
          project_id: projectId,
          owner_employee_id: assignee?.employee_id,
          // Every occurrence inherits these collaborators (design §6 recurrence).
          collaborator_ids: collaborators.length ? collaborators.map((c) => c.employee_id) : undefined,
          checklist: checklist.length ? checklist : undefined,
          evidence_requirements: evidence.length ? evidence : undefined,
          recurrence_rule: rule,
          recurrence_anchor: new Date(form.plannedStart).toISOString(),
          recurrence_tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          default_planned_duration_minutes: durationMin,
          // The chosen effort applies to EVERY occurrence (Phase 2).
          ...(form.effortSize ? { effort_size: form.effortSize as EffortSize } : {}),
        });
        const startsInFuture = new Date(form.plannedStart).getTime() > Date.now();
        toast.success(startsInFuture
          ? t('recurring_created', { defaultValue: 'Recurring task created — the first one is scheduled for its start date.' }) as string
          : t('recurring_created_now', { defaultValue: 'Recurring task created — first one is ready now.' }) as string);
        setForm({}); setChecklistText(''); setEvidenceText(''); setNewProjectName(''); setAssignee(null); setCollaborators([]);
        onCreated();
        onClose();
        return;
      }

      const item = await createWorkItem({
        title: form.title.trim(),
        description: form.description || undefined,
        priority: form.priority || undefined,
        project_id: projectId,
        template_id: form.templateId || undefined,
        goal_id: form.goalId || undefined,
        planned_start_at: form.plannedStart ? new Date(form.plannedStart).toISOString() : undefined,
        planned_end_at: form.plannedEnd ? new Date(form.plannedEnd).toISOString() : undefined,
        checklist: checklist.length ? checklist : undefined,
        evidence_requirements: evidence.length ? evidence : undefined,
        ...(form.effortSize ? { effort_size: form.effortSize as EffortSize } : {}),
      });
      if (assignee) {
        rememberPersonName(assignee.employee_id, assignee.display_name);
        await assignWorkItem(item.id, assignee.employee_id, assignee.user_id || undefined);
      }
      // Collaborators are best-effort after create: one refusal (scope, policy)
      // surfaces its backend message and never blocks the others or the item.
      for (const c of collaborators) {
        try {
          rememberPersonName(c.employee_id, c.display_name);
          await addWorkContributor(item.id, c.employee_id);
        } catch (e) {
          toast.error(getApiErrorMessage(e, t('collab_add_failed', { defaultValue: 'Could not add {{name}} as a collaborator.', name: c.display_name }) as string));
        }
      }
      await flushAssets(item.id);
      toast.success(assignee
        ? t('created_assigned', { defaultValue: 'Work item created and assigned.' }) as string
        : t('created', { defaultValue: 'Work item created (draft).' }) as string);
      setForm({}); setChecklistText(''); setEvidenceText(''); setNewProjectName(''); setPendingAssets([]); setAssignee(null); setCollaborators([]);
      onCreated();
      onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('create_failed', { defaultValue: 'Could not create the work item.' })));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/20 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg bg-background border border-border p-5 space-y-3 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <h3 className="text-h4 text-charcoal">{t('new', { defaultValue: 'New work item' })}</h3>
        <div className="grid grid-cols-2 gap-3">
          {templates.length > 0 && (
            <div className="col-span-2">
              <Label>{t('template', { defaultValue: 'Work type (prefills checklist + evidence — still editable)' })}</Label>
              <select className={sel} value={form.templateId || ''} onChange={(e) => applyTemplate(e.target.value)}>
                <option value="">{t('no_template', { defaultValue: 'Start blank' })}</option>
                {templates.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}</option>)}
              </select>
            </div>
          )}
          <div className="col-span-2">
            <Label htmlFor="create-title">{t('title', { defaultValue: 'Title' })}</Label>
            <Input id="create-title" value={form.title || ''} onChange={set('title')} />
          </div>
          <div className="col-span-2">
            <Label>{t('description', { defaultValue: 'Description' })}</Label>
            <Textarea rows={2} value={form.description || ''} onChange={set('description')} />
          </div>
          <div>
            <Label htmlFor="create-assignee">{t('assign_to', { defaultValue: 'Assign to' })}</Label>
            <PeoplePicker id="create-assignee" value={assignee} fetchPage={fetchAssignees} disabled={busy}
              onChange={(p) => { setAssignee(p); setForm((f) => ({ ...f, goalId: '' })); loadGoals(p?.employee_id || ''); }}
              placeholder={t('assign_later', { defaultValue: 'Assign later (draft)' }) as string}
              emptyHint={t('assign_later_hint', { defaultValue: 'Leave empty to create a draft and assign later.' }) as string} />
          </div>
          <div>
            <Label>{t('project', { defaultValue: 'Project' })}</Label>
            <select className={sel} value={form.projectId || ''} onChange={set('projectId')}>
              <option value="">{t('no_project', { defaultValue: 'No project' })}</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              <option value="__new__">{t('new_project', { defaultValue: '+ New project…' })}</option>
            </select>
          </div>
          {form.projectId === '__new__' && (
            <div className="col-span-2">
              <Label>{t('project_name', { defaultValue: 'New project name' })}</Label>
              <Input value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)}
                placeholder={t('project_ph', { defaultValue: 'e.g. Depot rollout Q4' }) as string} />
            </div>
          )}
          {assignee && goals.length > 0 && (
            <div className="col-span-2">
              <Label>{t('goal', { defaultValue: 'Contributes to goal (optional — shows as evidence, never auto-progress)' })}</Label>
              <select className={sel} value={form.goalId || ''} onChange={set('goalId')}>
                <option value="">{t('no_goal', { defaultValue: 'No goal' })}</option>
                {goals.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
              </select>
            </div>
          )}
          <div>
            <Label>{t('priority', { defaultValue: 'Priority' })}</Label>
            <select className={sel} value={form.priority || 'normal'} onChange={set('priority')}>
              {['low', 'normal', 'high', 'urgent'].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="create-effort">{t('effort', { defaultValue: 'Effort' })}</Label>
            <EffortSelect id="create-effort" className={sel} value={(form.effortSize as EffortSize) || ''}
              onChange={(v) => setForm((f) => ({ ...f, effortSize: v || '' }))} />
            <p className="text-xs text-muted-foreground mt-1">
              {form.repeat
                ? t('effort_recurring_hint', { defaultValue: 'Relative size for workload planning — not hours. Applies to every generated task.' })
                : t('effort_help', { defaultValue: 'Relative size for workload planning — not hours.' })}
            </p>
          </div>
          {/* Collaborators (optional): many contributors, still ONE accountable
              owner. Added after create for a one-off item; for a repeating
              series they become the template's default collaborators and every
              spawned occurrence inherits them. Hidden when the policy disables them. */}
          {policy?.allow_collaborators !== false && (
            <div className="col-span-2">
              <Label htmlFor="create-collaborators">{t('collaborators', { defaultValue: 'Collaborators (optional)' })}</Label>
              <PeoplePicker id="create-collaborators" multiple value={collaborators} onChange={setCollaborators}
                fetchPage={fetchCollaborators} disabled={busy}
                exclude={assignee ? [assignee.employee_id] : undefined}
                policyDisabledMessage={t('picker_policy_disabled_collab', { defaultValue: "Your company hasn't enabled collaborators" }) as string}
                placeholder={t('collaborators_ph', { defaultValue: 'Add people who will contribute…' }) as string} />
              {form.repeat && collaborators.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('collaborators_recurring_hint', { defaultValue: 'Every generated task will include these collaborators.' })}
                </p>
              )}
            </div>
          )}
          <div className="col-span-2">
            <Label>{t('planned_start', { defaultValue: 'Planned start' })}</Label>
            <DateTimeField
              value={form.plannedStart || ''}
              onChange={(v) => setForm((f) => {
                // Keep the window coherent: if end is now before start, clear it.
                const next: Record<string, string> = { ...f, plannedStart: v };
                if (f.plannedEnd && v && new Date(f.plannedEnd) < new Date(v)) next.plannedEnd = '';
                return next;
              })}
            />
          </div>
          <div className="col-span-2">
            <Label>{t('planned_end', { defaultValue: 'Planned end' })}</Label>
            <DateTimeField value={form.plannedEnd || ''} onChange={(v) => setForm((f) => ({ ...f, plannedEnd: v }))} />
          </div>

          {/* Repeat → saves as a recurring series (planned start = first occurrence). */}
          <div className="col-span-2 rounded-md border border-border p-3 space-y-2">
            <Label>{t('repeat', { defaultValue: 'Repeat' })}</Label>
            <select className={sel} value={form.repeat || ''} onChange={set('repeat')}>
              <option value="">{t('repeat_none', { defaultValue: "Doesn't repeat (one-off task)" })}</option>
              <option value="daily">{t('tpl_daily', { defaultValue: 'Daily' })}</option>
              <option value="weekly">{t('tpl_weekly', { defaultValue: 'Weekly' })}</option>
              <option value="monthly">{t('tpl_monthly', { defaultValue: 'Monthly' })}</option>
            </select>
            {form.repeat && (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">{t('tpl_every', { defaultValue: 'Every' })}</span>
                  <Input type="number" min={1} className="w-20" value={form.interval || '1'} onChange={set('interval')} />
                  <span className="text-muted-foreground">
                    {form.repeat === 'daily' && t('tpl_days', { defaultValue: 'day(s)' })}
                    {form.repeat === 'weekly' && t('tpl_weeks', { defaultValue: 'week(s)' })}
                    {form.repeat === 'monthly' && t('tpl_months', { defaultValue: 'month(s)' })}
                  </span>
                </div>
                {form.repeat === 'weekly' && (
                  <div className="flex flex-wrap gap-1">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => {
                      const days = (form.weekdays || '').split(',').filter(Boolean);
                      const on = days.includes(String(i));
                      return (
                        <button key={d} type="button"
                          className={`px-2 py-1 rounded text-xs border ${on ? 'bg-primary-500 text-white border-primary-500' : 'border-border text-muted-foreground'}`}
                          onClick={() => {
                            const s = new Set(days);
                            if (on) s.delete(String(i)); else s.add(String(i));
                            setForm((f) => ({ ...f, weekdays: Array.from(s).sort().join(',') }));
                          }}>{d}</button>
                      );
                    })}
                  </div>
                )}
                {form.repeat === 'monthly' && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">{t('tpl_on_day', { defaultValue: 'On day' })}</span>
                    <Input type="number" min={0} max={31} className="w-20" value={form.byMonthday || '0'} onChange={set('byMonthday')} />
                    <span className="text-xs text-muted-foreground">{t('tpl_monthday_hint', { defaultValue: '(0 = same day as the start date)' })}</span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {t('repeat_anchor_hint', { defaultValue: 'Uses Planned start as the first occurrence and time of day. Set a Planned start above.' })}
                </p>
              </>
            )}
          </div>

          {/* Attachments & assets — optional; keeps creation quick. Heavy files
              go via a link (Drive/Figma/…); normal files upload to KaziPeople. */}
          <div className="col-span-2 space-y-2">
            <Label>{t('assets', { defaultValue: 'Attachments & assets' })}</Label>
            <p className="text-xs text-muted-foreground">{t('assets_hint', { defaultValue: 'Add files or links needed for this work (optional).' })}</p>
            {form.repeat ? (
              <p className="text-xs text-muted-foreground">{t('assets_recurring', { defaultValue: 'Add assets on each task once the series starts.' })}</p>
            ) : (
              <>
                {pendingAssets.length > 0 && (
                  <ul className="space-y-1">
                    {pendingAssets.map((a, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm rounded-md border border-border px-2 py-1">
                        {a.kind === 'link' ? <LinkIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <Paperclip className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                        <span className="truncate flex-1">{a.name}</span>
                        <span className="text-[11px] text-muted-foreground capitalize shrink-0">{a.purpose}</span>
                        <button type="button" aria-label="Remove" onClick={() => setPendingAssets((p) => p.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-600 shrink-0"><X className="w-3.5 h-3.5" /></button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border border-border cursor-pointer hover:bg-muted">
                    <Upload className="w-4 h-4" /> {t('upload_file', { defaultValue: 'Upload file' })}
                    <input type="file" className="hidden" onChange={stageFile} />
                  </label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setLinkOpen((v) => !v)}>
                    <LinkIcon className="w-4 h-4 mr-1" /> {t('add_link', { defaultValue: 'Add asset link' })}
                  </Button>
                </div>
                {linkOpen && (
                  <div className="grid grid-cols-2 gap-2 rounded-md border border-border p-2">
                    <Input placeholder={t('asset_name', { defaultValue: 'Asset name' }) as string} value={linkName} onChange={(e) => setLinkName(e.target.value)} />
                    <select className={sel} value={linkPurpose} onChange={(e) => setLinkPurpose(e.target.value as AssetPurpose)}>
                      <option value="reference">{t('purpose_reference', { defaultValue: 'Reference' })}</option>
                      <option value="deliverable">{t('purpose_deliverable', { defaultValue: 'Deliverable' })}</option>
                      <option value="supporting">{t('purpose_supporting', { defaultValue: 'Supporting' })}</option>
                    </select>
                    <Input className="col-span-2" placeholder="https://drive.google.com/…" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} />
                    <div className="col-span-2 flex justify-end"><Button type="button" size="sm" onClick={stageLink}>{t('common.add', { defaultValue: 'Add' })}</Button></div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button disabled={busy} onClick={submit}>
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {t('create', { defaultValue: 'Create' })}
          </Button>
        </div>
      </div>
    </div>
  );
};

/** Work-type template manager (work:manage): list + author reusable packs.
 * Same line formats as the create dialog — checklist one step per line,
 * evidence with photo:/document:/note: prefixes. */
export const ManageTemplatesDialog: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const { t } = useTranslation('work');
  const [templates, setTemplates] = useState<WorkTemplate[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [editing, setEditing] = useState<WorkTemplate | 'new' | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [checklistText, setChecklistText] = useState('');
  const [evidenceText, setEvidenceText] = useState('');
  const [busy, setBusy] = useState(false);
  // Default collaborators (design §6 recurrence): every spawned occurrence
  // inherits them. Stored as ids (the API shape); names resolve via the
  // directory-safe lookup so the multi picker can show its chips.
  const [defaultCollaboratorIds, setDefaultCollaboratorIds] = useState<string[]>([]);
  const { nameOf: collaboratorNameOf } = usePeopleNames(defaultCollaboratorIds);
  const fetchCollaborators = useEligibleAssignees('collaborate');
  const { policy: workPolicy } = useWorkPolicy();
  const defaultCollaborators: Person[] = defaultCollaboratorIds.map((employee_id) => ({ employee_id, display_name: collaboratorNameOf(employee_id) }));
  // Series assignee: who every generated occurrence goes to. It was never on
  // this form — and saving the form used to wipe it server-side, so a series
  // could neither be pointed at the right person nor kept pointed at anyone.
  const [defaultAssigneeId, setDefaultAssigneeId] = useState<string | null>(null);
  const { nameOf: assigneeNameOf } = usePeopleNames([defaultAssigneeId]);
  const fetchSeriesAssignees = useEligibleAssignees('assign');
  const defaultAssignee: Person | null = defaultAssigneeId
    ? { employee_id: defaultAssigneeId, display_name: assigneeNameOf(defaultAssigneeId) }
    : null;

  const load = useCallback(() => {
    listWorkTemplates(true).then(setTemplates)
      .catch((e) => toast.error(getApiErrorMessage(e, t('tpl_load_failed', { defaultValue: 'Could not load templates.' }))));
  }, [t]);
  useEffect(() => { if (open) { setEditing(null); load(); } }, [open, load]);
  if (!open) return null;

  const startEdit = (tpl: WorkTemplate | 'new') => {
    setEditing(tpl);
    setDefaultCollaboratorIds(tpl === 'new' ? [] : (tpl.default_collaborator_ids || []));
    setDefaultAssigneeId(tpl === 'new' ? null : (tpl.default_assignee_id || null));
    if (tpl === 'new') {
      setForm({ priority: 'normal', review: 'inherit' });
      setChecklistText('');
      setEvidenceText('');
    } else {
      setForm({
        name: tpl.name, description: tpl.description || '', priority: tpl.priority,
        defaultEffort: tpl.default_effort_size || '',
        active: tpl.active ? '1' : '', reqSub: tpl.require_subitems_complete ? '1' : '',
        review: tpl.completion_review || 'inherit',
        repeat: tpl.recurrence_rule?.freq || '',
        interval: String(tpl.recurrence_rule?.interval || 1),
        weekdays: (tpl.recurrence_rule?.weekdays || []).join(','),
        byMonthday: String(tpl.recurrence_rule?.by_monthday || 0),
        anchor: isoToLocalInput(tpl.recurrence_anchor || undefined),
        durationMin: tpl.default_planned_duration_minutes ? String(tpl.default_planned_duration_minutes) : '',
        recActive: tpl.recurrence_active ? '1' : '',
      });
      setChecklistText(checklistToLines(tpl.checklist));
      setEvidenceText(evidenceToLines(tpl.evidence));
    }
  };

  const save = async () => {
    if (!form.name?.trim()) {
      toast.error(t('tpl_name_required', { defaultValue: 'A template name is required.' }));
      return;
    }
    // Recurrence validation + assembly.
    let recurrence_rule: import('@/services/workService').RecurrenceRule | null = null;
    let recurrence_active = false;
    let recurrence_anchor: string | undefined;
    let recurrence_tz: string | undefined;
    let default_planned_duration_minutes: number | undefined;
    if (form.repeat) {
      const freq = form.repeat as 'daily' | 'weekly' | 'monthly';
      const weekdays = (form.weekdays || '').split(',').filter(Boolean).map(Number);
      if (freq === 'weekly' && weekdays.length === 0) {
        toast.error(t('tpl_pick_weekday', { defaultValue: 'Pick at least one weekday for a weekly repeat.' }));
        return;
      }
      if (form.recActive === '1' && !form.anchor) {
        toast.error(t('tpl_anchor_required', { defaultValue: 'A recurring template needs a start date.' }));
        return;
      }
      recurrence_rule = {
        freq,
        interval: Math.max(1, parseInt(form.interval || '1', 10) || 1),
        ...(freq === 'weekly' ? { weekdays } : {}),
        ...(freq === 'monthly' ? { by_monthday: parseInt(form.byMonthday || '0', 10) || 0 } : {}),
      };
      recurrence_active = form.recActive === '1';
      recurrence_anchor = form.anchor ? new Date(form.anchor).toISOString() : undefined;
      recurrence_tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      default_planned_duration_minutes = form.durationMin ? parseInt(form.durationMin, 10) : undefined;
    }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description || undefined,
        priority: form.priority || 'normal',
        checklist: parseChecklistLines(checklistText),
        evidence: parseEvidenceLines(evidenceText),
        require_subitems_complete: form.reqSub === '1',
        // Does finishing work of this type need a reviewer? `inherit` = whatever
        // the company policy says (Access & Assignment → Completion).
        completion_review: ((form.review as CompletionReview) || 'inherit'),
        active: editing !== 'new' && editing ? form.active === '1' : true,
        recurrence_rule,
        recurrence_active,
        recurrence_anchor,
        recurrence_tz,
        default_planned_duration_minutes,
        default_collaborator_ids: defaultCollaboratorIds,
        // Only a repeating template shows (and so owns) the series assignee.
        // Otherwise the key is left OUT and the server keeps what is stored —
        // an absent key is "unchanged", an explicit null is "unassigned".
        ...(recurrence_rule ? { default_assignee_id: defaultAssigneeId } : {}),
        // "Not sized" is sent as null so an edit can clear a previous default.
        default_effort_size: (form.defaultEffort as EffortSize) || null,
      };
      if (editing === 'new') await createWorkTemplate(body);
      else if (editing) await updateWorkTemplate(editing.id, body);
      toast.success(t('tpl_saved', { defaultValue: 'Template saved.' }));
      setEditing(null);
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('tpl_save_failed', { defaultValue: 'Could not save the template.' })));
    } finally {
      setBusy(false);
    }
  };

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const sel = 'w-full rounded-md border border-border bg-background text-foreground text-sm px-2.5 py-2';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/20 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg bg-background border border-border p-5 space-y-3 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-h4 text-charcoal">{t('tpl_title', { defaultValue: 'Work templates' })}</h3>
          {!editing && (
            <Button size="sm" onClick={() => startEdit('new')}>
              {t('tpl_new', { defaultValue: 'New template' })}
            </Button>
          )}
        </div>

        {!editing && (
          <div className="space-y-1">
            {templates.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t('tpl_none', { defaultValue: 'No templates yet — define one so managers stop retyping the same checklist and evidence pack.' })}
              </p>
            )}
            {templates.map((tpl) => (
              <button key={tpl.id} type="button" onClick={() => startEdit(tpl)}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-left hover:bg-muted/50">
                <span className="min-w-0">
                  <span className={tpl.active ? 'text-foreground' : 'text-muted-foreground line-through'}>{tpl.name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {tpl.checklist.length} {t('tpl_steps', { defaultValue: 'steps' })} · {tpl.evidence.length} {t('tpl_evidence', { defaultValue: 'evidence' })}
                  </span>
                </span>
                <span className="flex items-center gap-1 shrink-0">
                  {tpl.recurrence_active && (
                    <Badge variant="secondary" className="bg-info-soft text-info-strong">{t('tpl_recurring', { defaultValue: 'recurring' })}</Badge>
                  )}
                  <Badge variant="secondary" className="bg-muted text-muted-foreground">{tpl.priority}</Badge>
                </span>
              </button>
            ))}
          </div>
        )}

        {editing && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>{t('tpl_name', { defaultValue: 'Name' })}</Label>
              <Input value={form.name || ''} onChange={set('name')}
                placeholder={t('tpl_name_ph', { defaultValue: 'e.g. Site installation' }) as string} />
            </div>
            <div className="col-span-2">
              <Label>{t('description', { defaultValue: 'Description' })}</Label>
              <Input value={form.description || ''} onChange={set('description')} />
            </div>
            <div>
              <Label>{t('priority', { defaultValue: 'Priority' })}</Label>
              <select className={sel} value={form.priority || 'normal'} onChange={set('priority')}>
                {['low', 'normal', 'high', 'urgent'].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="tpl-default-effort">{t('tpl_default_effort', { defaultValue: 'Default effort' })}</Label>
              <EffortSelect id="tpl-default-effort" className={sel} value={(form.defaultEffort as EffortSize) || ''}
                ariaLabel={t('tpl_default_effort', { defaultValue: 'Default effort' }) as string}
                onChange={(v) => setForm((f) => ({ ...f, defaultEffort: v || '' }))} />
              <p className="text-xs text-muted-foreground mt-1">{t('tpl_default_effort_help', { defaultValue: 'Every task created from this template starts with this size — relative units, not hours.' })}</p>
            </div>
            <div>
              <Label htmlFor="tpl-review">{t('tpl_review', { defaultValue: 'Needs review' })}</Label>
              <select id="tpl-review" className={sel} value={form.review || 'inherit'} onChange={set('review')}>
                <option value="inherit">{t('tpl_review_inherit', { defaultValue: 'Company default' })}</option>
                <option value="required">{t('tpl_review_required', { defaultValue: 'Always' })}</option>
                <option value="not_required">{t('tpl_review_not_required', { defaultValue: 'Never' })}</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">{t('tpl_review_help', { defaultValue: 'Always sends finished work of this type to a reviewer; Never lets the assignee complete it themselves. Required checklist items and evidence still apply either way.' })}</p>
            </div>
            {editing !== 'new' && (
              <div>
                <Label>{t('tpl_status', { defaultValue: 'Status' })}</Label>
                <select className={sel} value={form.active || ''} onChange={set('active')}>
                  <option value="1">{t('tpl_active', { defaultValue: 'Active' })}</option>
                  <option value="">{t('tpl_inactive', { defaultValue: 'Deactivated (hidden from pickers)' })}</option>
                </select>
              </div>
            )}
            <div className="col-span-2">
              <Label>{t('checklist_lines', { defaultValue: 'Checklist (one required step per line)' })}</Label>
              <Textarea rows={3} value={checklistText} onChange={(e) => setChecklistText(e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label>{t('ev_lines', { defaultValue: 'Required completion evidence (one per line; prefix with photo: / document: / note:)' })}</Label>
              <Textarea rows={3} value={evidenceText} onChange={(e) => setEvidenceText(e.target.value)} />
            </div>
            <label className="col-span-2 flex items-center gap-2 text-sm text-foreground">
              <input type="checkbox" className="h-4 w-4 accent-accent"
                checked={form.reqSub === '1'}
                onChange={(e) => setForm((f) => ({ ...f, reqSub: e.target.checked ? '1' : '' }))} />
              {t('tpl_req_sub', { defaultValue: 'Require every sub-item to be completed before submission' })}
            </label>

            {/* Recurrence: turns this template into a repeating task generator. */}
            <div className="col-span-2 rounded-md border border-border p-3 space-y-2">
              <Label>{t('tpl_repeat', { defaultValue: 'Repeat (auto-create this task on a schedule)' })}</Label>
              <select className={sel} value={form.repeat || ''} onChange={set('repeat')}>
                <option value="">{t('tpl_repeat_none', { defaultValue: "Doesn't repeat" })}</option>
                <option value="daily">{t('tpl_daily', { defaultValue: 'Daily' })}</option>
                <option value="weekly">{t('tpl_weekly', { defaultValue: 'Weekly' })}</option>
                <option value="monthly">{t('tpl_monthly', { defaultValue: 'Monthly' })}</option>
              </select>
              {form.repeat && (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">{t('tpl_every', { defaultValue: 'Every' })}</span>
                    <Input type="number" min={1} className="w-20" value={form.interval || '1'} onChange={set('interval')} />
                    <span className="text-muted-foreground">
                      {form.repeat === 'daily' && t('tpl_days', { defaultValue: 'day(s)' })}
                      {form.repeat === 'weekly' && t('tpl_weeks', { defaultValue: 'week(s)' })}
                      {form.repeat === 'monthly' && t('tpl_months', { defaultValue: 'month(s)' })}
                    </span>
                  </div>
                  {form.repeat === 'weekly' && (
                    <div className="flex flex-wrap gap-1">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => {
                        const days = (form.weekdays || '').split(',').filter(Boolean);
                        const on = days.includes(String(i));
                        return (
                          <button key={d} type="button"
                            className={`px-2 py-1 rounded text-xs border ${on ? 'bg-primary-500 text-white border-primary-500' : 'border-border text-muted-foreground'}`}
                            onClick={() => {
                              const s = new Set(days);
                              if (on) s.delete(String(i)); else s.add(String(i));
                              setForm((f) => ({ ...f, weekdays: Array.from(s).sort().join(',') }));
                            }}>{d}</button>
                        );
                      })}
                    </div>
                  )}
                  {form.repeat === 'monthly' && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">{t('tpl_on_day', { defaultValue: 'On day' })}</span>
                      <Input type="number" min={0} max={31} className="w-20" value={form.byMonthday || '0'} onChange={set('byMonthday')} />
                      <span className="text-xs text-muted-foreground">{t('tpl_monthday_hint', { defaultValue: '(0 = same day as the start date)' })}</span>
                    </div>
                  )}
                  <div>
                    <Label>{t('tpl_anchor', { defaultValue: 'Starts on (first occurrence + time of day)' })}</Label>
                    <DateTimeField value={form.anchor || ''} onChange={(v) => setForm((f) => ({ ...f, anchor: v }))} />
                  </div>
                  <div>
                    <Label>{t('tpl_duration', { defaultValue: 'Planned duration (minutes, optional)' })}</Label>
                    <Input type="number" min={0} className="w-32" value={form.durationMin || ''} onChange={set('durationMin')}
                      placeholder="e.g. 60" />
                  </div>
                  <div>
                    <Label htmlFor="tpl-series-assignee">{t('tpl_series_assignee', { defaultValue: 'Assign each occurrence to' })}</Label>
                    <PeoplePicker id="tpl-series-assignee" value={defaultAssignee}
                      onChange={(p) => {
                        if (p) rememberPersonName(p.employee_id, p.display_name);
                        setDefaultAssigneeId(p ? p.employee_id : null);
                      }}
                      fetchPage={fetchSeriesAssignees} disabled={busy}
                      placeholder={t('tpl_series_assignee_ph', { defaultValue: 'Nobody — occurrences wait unassigned' }) as string} />
                    <p className="text-xs text-muted-foreground mt-1">{t('tpl_series_assignee_help', { defaultValue: 'Changes apply to occurrences created from now on. Ones already created keep their assignee — reassign those from the work item.' })}</p>
                  </div>
                  {/* Default collaborators: inherited by every generated task
                      (the default assignee stays the accountable owner). */}
                  {workPolicy?.allow_collaborators !== false && (
                    <div>
                      <Label htmlFor="tpl-default-collaborators">{t('tpl_default_collaborators', { defaultValue: 'Default collaborators (added to every generated task)' })}</Label>
                      <PeoplePicker id="tpl-default-collaborators" multiple value={defaultCollaborators}
                        onChange={(people) => {
                          people.forEach((p) => rememberPersonName(p.employee_id, p.display_name));
                          setDefaultCollaboratorIds(people.map((p) => p.employee_id));
                        }}
                        fetchPage={fetchCollaborators} disabled={busy}
                        policyDisabledMessage={t('picker_policy_disabled_collab', { defaultValue: "Your company hasn't enabled collaborators" }) as string}
                        placeholder={t('collaborators_ph', { defaultValue: 'Add people who will contribute…' }) as string} />
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm text-foreground">
                    <input type="checkbox" className="h-4 w-4 accent-accent"
                      checked={form.recActive === '1'}
                      onChange={(e) => setForm((f) => ({ ...f, recActive: e.target.checked ? '1' : '' }))} />
                    {t('tpl_rec_active', { defaultValue: 'Active — generate tasks on this schedule' })}
                  </label>
                </>
              )}
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(null)}>{t('common.back', { defaultValue: 'Back' })}</Button>
              <Button disabled={busy} onClick={save}>
                {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                {t('tpl_save', { defaultValue: 'Save template' })}
              </Button>
            </div>
          </div>
        )}

        {!editing && (
          <div className="flex justify-end pt-1">
            <Button variant="ghost" onClick={onClose}>{t('common.close', { defaultValue: 'Close' })}</Button>
          </div>
        )}
      </div>
    </div>
  );
};
